import React, { useState, useEffect, useRef, Suspense, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mic, MicOff, Volume2, VolumeX, Download, Settings, Maximize2, Minimize2, Send, X, Clock, CreditCard } from "lucide-react";
import { io } from "socket.io-client";
import { Experience } from "../../components/Experience";
import { Canvas } from "@react-three/fiber";
import DashboardLayout from "../../layouts/DashboardLayout";
import { TimeTracker } from "../../components/TimeTracker";
import MicrophonePermissionPopup from "../../components/MicrophonePermissionPopup";
import CognitiveModelReconstructionPopup from "../../components/CognitiveModelReconstructionPopup";
import api from "../../api";
import Toast from "../../components/ui/Toast";

function ChatSession() {
  const location = useLocation();
  const navigate = useNavigate();
  const [creditUsed, setCreditUsed] = useState(0);
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false); // [NEW] Mute state
  const [replyText, setReplyText] = useState(null);
  const [speechMarks, setSpeechMarks] = useState(null);
  const [isCrying, setIsCrying] = useState(false);
  const [lowBalance, setLowBalance] = useState(false);
  const [micConflictMessage, setMicConflictMessage] = useState(null);
  const [breadcrumbLevel, setBreadcrumbLevel] = useState("none");
  const [mood, setMood] = useState("neutral");
  const [intensity, setIntensity] = useState(0);
  const [trust, setTrust] = useState(0);
  // insight state
  const [doctorStyleState, setDoctorStyleState] = useState(null);
  const [therapeuticAlliance, setTherapeuticAlliance] = useState(null);
  const [triggerSystem, setTriggerSystem] = useState(null);
  const [copingMechanisms, setCopingMechanisms] = useState(null);
  const [behavioralPatterns, setBehavioralPatterns] = useState(null);
  const [progressTracker, setProgressTracker] = useState(null);
  const [sessionMetrics, setSessionMetrics] = useState(null);
  // Retrieve chat state from navigation or localStorage
  const state = location.state || JSON.parse(localStorage.getItem("chatState") || "{}");
  const { patient_profile, consultation_id, doctor_id } = state;
  const isCustomPersona = useMemo(() => {
    try {
      const vpRaw = patient_profile?.virtual_profile;
      if (!vpRaw) return false;
      const vp = typeof vpRaw === "string" ? JSON.parse(vpRaw) : vpRaw;
      // Only check for is_custom_persona flag - cognitive_model exists in both random and custom personas
      return !!(vp?.is_custom_persona);
    } catch {
      return false;
    }
  }, [patient_profile]);
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAvatarFullscreen, setIsAvatarFullscreen] = useState(false);
  const [isSessionInsightsMinimized, setIsSessionInsightsMinimized] = useState(false);
  const [showReconstructionPopup, setShowReconstructionPopup] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const patient_name = patient_profile.name;
  const patient_lang = patient_profile.lang;
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioChunks = useRef([]);
  const messagesEndRef = useRef(null);
  const gainNodeRef = useRef(null);
  const [micSensitivity, setMicSensitivity] = useState(1.5);
  const [silenceDetection, setsilenceDetection] = useState(15);
  const [sendMessageAfterSecond, setsendMessageAfterSecond] = useState(2); // Default: 2 seconds
  const [downloadRecording, setdownloadRecording] = useState(false);
  const [showSettings, setShowSettings] = useState(false); // For mobile settings toggle
  const maxRmsRef = useRef(0);
  const waveCanvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const rmsRef = useRef(0);
  const isProcessingRef = useRef(isProcessing);
  const isRecordingRef = useRef(isRecording);
  const isManualRecordingStop = useRef(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const messageRef = useRef(message);
  const socketRef = useRef(null);
  const socketConnectionIdsRef = useRef({ patientId: null, consultationId: null, doctorId: null });
  const isSettingUpSocketRef = useRef(false);
  const recognitionRef = useRef(null);
  const bufferRef = useRef("");
  const pendingInterimRef = useRef(""); // Store interim results that might be lost on abort
  const lastProcessedFinalIndexRef = useRef(-1); // Track the last processed final result index to avoid duplicates
  const lastProcessedFinalTextRef = useRef(""); // Track the last processed final text to extract only new words when recognition restarts
  let animationID = null
  const sendMessageAfterSecondRef = useRef(sendMessageAfterSecond);
  const processingTimeoutRef = useRef(null);
  const isRecognitionStartingRef = useRef(false);
  const silentDetectionRef = useRef(null);
  const recognitionRestartTimeoutsRef = useRef([]); // Track all pending recognition restart timeouts
  const recognitionRestartCountRef = useRef(0); // Track consecutive restart attempts
  const lastRecognitionEndTimeRef = useRef(0); // Track when recognition last ended
  const recognitionStartTimeRef = useRef(0); // Track when recognition last started successfully
  const currentTTSAudioRef = useRef(null);
  const audioNodesRef = useRef({ source: null, analyser: null });
  const isTTSPlayingRef = useRef(false); // Track if TTS is currently playing
  const currentTTSMediaSourceRef = useRef(null); // Track current TTS MediaSource
  const isStreamingTTSRef = useRef(false); // Track if LLM is still streaming chunks
  const isFetchingTTSRef = useRef(false); // [FIX] Track if we are currently fetching TTS audio (prevent mic unmute during fetch gap)
  const isTTSQueuePlayingRef = useRef(false); // Track if queue processor is running
  const cryingAudioRef = useRef(null); // Reference for crying sound effect
  const isCryingSoundPlayingRef = useRef(false); // Track if crying sound is currently playing to prevent duplicates
  const [showNavigationConfirm, setShowNavigationConfirm] = useState(false);
  const pendingNavigationRef = useRef(null);
  const shouldBlockNavigationRef = useRef(true); // Flag to allow navigation when user confirms or uses End Session
  const [showMicrophonePermissionPopup, setShowMicrophonePermissionPopup] = useState(false);
  const isMutedRef = useRef(isMuted);
  const [toastNotification, setToastNotification] = useState(null);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Streaming TTS state
  const ttsQueueRef = useRef([]);
  const messageAccumulatorRef = useRef("");

  /**
   * Play crying/weeping sound effect when patient is crying
   * Randomly selects an audio file based on patient's gender
   * Returns a Promise that resolves when the audio finishes playing
   */
  function playCryingSound() {
    // Prevent duplicate playback - if already playing, return existing promise
    if (isCryingSoundPlayingRef.current && cryingAudioRef.current) {
      console.log("[Crying Sound] Already playing, skipping duplicate call");
      return new Promise((resolve) => {
        // Wait for the existing audio to finish
        if (cryingAudioRef.current) {
          cryingAudioRef.current.addEventListener('ended', () => {
            resolve();
          }, { once: true });
        } else {
          resolve();
        }
      });
    }

    return new Promise((resolve, reject) => {
      try {
        // Mark as playing to prevent duplicates
        isCryingSoundPlayingRef.current = true;

        // Stop any existing crying sound first
        if (cryingAudioRef.current) {
          try {
            cryingAudioRef.current.pause();
            cryingAudioRef.current.currentTime = 0;
          } catch (e) {
            // Ignore errors when stopping
          }
          cryingAudioRef.current = null;
        }

        // Get patient gender from profile
        if (!patient_profile || !patient_profile.gender) {
          console.warn("[Crying Sound] Patient gender not available, using default");
          resolve(); // Resolve immediately if no gender available
          return;
        }

        // Normalize gender to lowercase for file matching
        const gender = patient_profile.gender.toLowerCase();
        const genderPrefix = gender === "male" || gender === "m" ? "male" : "female";

        // Define available audio files for each gender
        // These should match the files in frontend/public/media/audio/
        const availableFiles = {
          female: [
            "female-sobbing-1.mp3",
            "female-sobbing-2.mp3",
            "female-sobbing-3.mp3",
            "female-sobbing-4.mp3"
          ],
          male: [
            "male-sobbing-1.mp3"
          ]
        };

        // Get files for this gender
        const genderFiles = availableFiles[genderPrefix] || availableFiles.female; // Default to female if gender not recognized

        if (genderFiles.length === 0) {
          console.error(`[Crying Sound] No audio files available for gender: ${genderPrefix}`);
          resolve(); // Resolve immediately if no files available
          return;
        }

        // Randomly select one file
        const randomIndex = Math.floor(Math.random() * genderFiles.length);
        const selectedFile = genderFiles[randomIndex];
        const audioPath = `/media/audio/${selectedFile}`;

        console.log(`[Crying Sound] Selected ${selectedFile} for ${genderPrefix} patient (random index: ${randomIndex})`);

        // Create HTML5 Audio element
        const audio = new Audio(audioPath);

        // Set volume (adjust as needed, 0.0 to 1.0)
        audio.volume = 0.7;

        // Handle errors
        audio.onerror = (e) => {
          console.error(`[Crying Sound] Error loading audio file ${audioPath}:`, e);
          cryingAudioRef.current = null;
          isCryingSoundPlayingRef.current = false; // Reset flag on error
          reject(e); // Reject promise on error
        };

        // Clean up after playback and resolve promise
        audio.onended = () => {
          console.log(`[Crying Sound] Finished playing ${selectedFile}`);
          cryingAudioRef.current = null;
          isCryingSoundPlayingRef.current = false; // Reset flag when finished
          resolve(); // Resolve promise when audio finishes
        };

        // Store reference
        cryingAudioRef.current = audio;

        // Play the audio
        audio.play().catch(err => {
          console.error(`[Crying Sound] Error playing audio:`, err);
          cryingAudioRef.current = null;
          isCryingSoundPlayingRef.current = false; // Reset flag on play error
          reject(err); // Reject promise on play error
        });

        console.log(`[Crying Sound] Playing ${selectedFile} for ${genderPrefix} patient`);
      } catch (error) {
        console.error("[Crying Sound] Error playing crying sound:", error);
        cryingAudioRef.current = null;
        isCryingSoundPlayingRef.current = false; // Reset flag on any error
        reject(error); // Reject promise on any error
      }
    });
  }

  const ignoredWords = [
    "Thank you for watching and don't forget to like and subscribe!",
    "You",
    "Ok",
    "Yaw it, yaw it",
    "Yaw it",
    "Don't forget to like andshare Thanks for watching",
    "Don't forget to like and share Thanks for watching",
    "Don't forget to like",
    "Thanks for watching"
  ]

  useEffect(() => {
    sendMessageAfterSecondRef.current = sendMessageAfterSecond;
  }, [sendMessageAfterSecond]);

  // Redirect if profiles are missing
  // Store state in ref to avoid recreating on every render
  const stateRefForStorage = useRef(state);
  useEffect(() => {
    stateRefForStorage.current = state;
  }, [state]);

  useEffect(() => {
    if (!patient_profile || !consultation_id || !doctor_id) {
      alert("No profile selected. Redirecting to dashboard.");
      shouldBlockNavigationRef.current = false; // Allow navigation for missing profiles
      navigate("/appointment");
    } else {
      localStorage.setItem("chatState", JSON.stringify(stateRefForStorage.current));
      // Reset navigation blocking when component mounts or state changes
      shouldBlockNavigationRef.current = true;
    }
    // Only depend on IDs to prevent infinite loops
  }, [doctor_id, consultation_id, patient_profile?.id, navigate]);

  // Intercept React Router navigation by monitoring location changes
  const prevLocationRef = useRef(location.pathname);
  const isRevertingRef = useRef(false); // Flag to prevent infinite loops

  useEffect(() => {
    // Skip if we're currently reverting navigation to prevent infinite loop
    if (isRevertingRef.current) {
      isRevertingRef.current = false;
      prevLocationRef.current = location.pathname;
      return;
    }

    // If we're on chat and location changed, it means navigation was attempted
    if (
      prevLocationRef.current === '/chat' &&
      location.pathname !== '/chat' &&
      shouldBlockNavigationRef.current &&
      !pendingNavigationRef.current
    ) {
      // Navigation was attempted, show confirmation and revert
      setShowNavigationConfirm(true);
      pendingNavigationRef.current = {
        type: 'router',
        targetPath: location.pathname
      };
      // Revert navigation by going back in history
      isRevertingRef.current = true; // Set flag before reverting
      window.history.back();
    } else {
      // Update ref to current location
      prevLocationRef.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing, isManualRecordingStop]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    };
  }, []);

  // Clear speechMarks on window resize/visibility change if TTS is not playing
  // This prevents unwanted lip movement when window is minimized/maximized
  useEffect(() => {
    const handleResize = () => {
      if (!isTTSPlayingRef.current) {
        setSpeechMarks(null);
        setReplyText(null);
      }
    };

    const handleVisibilityChange = () => {
      if (!isTTSPlayingRef.current) {
        setSpeechMarks(null);
        setReplyText(null);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const updateAvatarFullscreen = async (nextState) => {
    try {
      if (nextState) {
        if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } else if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen toggle failed:", err);
    } finally {
      setIsAvatarFullscreen(nextState);
    }
  };

  // Sync React state with browser fullscreen state (handles ESC key press)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      setIsAvatarFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => { });
      }
    };
  }, []);

  // SocketIO connection
  // Use refs to store stable values and prevent infinite loops
  const stateRef = useRef(state);
  const patientNameRef = useRef(patient_name);

  // Update refs when values change (but don't trigger effect)
  useEffect(() => {
    stateRef.current = state;
    patientNameRef.current = patient_name;
  }, [state, patient_name]);

  useEffect(() => {
    if (!patient_profile || !consultation_id || !doctor_id) return;

    const patientId = patient_profile?.id;
    const consultationId = consultation_id;
    const doctorId = doctor_id;

    // Prevent concurrent setup attempts
    if (isSettingUpSocketRef.current) {
      console.log("[Socket] Setup already in progress, skipping...");
      return;
    }

    // Check if we already have a connection for these exact IDs
    const currentIds = socketConnectionIdsRef.current;
    const idsMatch = (
      currentIds.patientId === patientId &&
      currentIds.consultationId === consultationId &&
      currentIds.doctorId === doctorId
    );

    if (socketRef.current && idsMatch) {
      // Connection already exists for these IDs, don't recreate
      console.log("[Socket] Connection already exists for these IDs, skipping recreation");
      // Just ensure socket state is set (in case it was cleared)
      if (!socket) {
        setSocket(socketRef.current);
      }
      return;
    }

    if (socketRef.current && !idsMatch) {
      console.log("[Socket] IDs changed, will recreate connection", {
        old: currentIds,
        new: { patientId, consultationId, doctorId }
      });
    }

    // Mark that we're setting up a connection
    isSettingUpSocketRef.current = true;

    // Clean up any existing connection before creating a new one
    if (socketRef.current) {
      console.log("[Socket] Cleaning up existing connection before creating new one");
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
    }

    // Update the connection IDs ref BEFORE creating new connection
    socketConnectionIdsRef.current = {
      patientId,
      consultationId,
      doctorId,
    };

    console.log("[Socket] Creating new Socket.IO connection");
    const s = io(process.env.REACT_APP_BACKEND_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    socketRef.current = s;
    setSocket(s);

    // Reset the setup flag after a brief delay to allow the connection to establish
    setTimeout(() => {
      isSettingUpSocketRef.current = false;
    }, 100);

    s.emit("join", { state: stateRef.current });

    const handleStatus = (data) => {
      setMessages((prev) => [...prev, { role: "system", content: data.msg }]);
    };
    s.on("status", handleStatus);


    // Function to stop any currently playing TTS audio
    function stopCurrentTTS() {
      console.log("[TTS] Stopping current TTS audio to play new message");

      // Stop and clean up the audio element
      if (currentTTSAudioRef.current) {
        try {
          const audio = currentTTSAudioRef.current;
          audio.pause();
          audio.currentTime = 0;
          // Remove event listeners to prevent cleanup from firing
          audio.onended = null;
          audio.onerror = null;
          // Revoke object URL to free memory
          if (audio.src && audio.src.startsWith('blob:')) {
            URL.revokeObjectURL(audio.src);
          }
          // Remove from DOM
          if (audio.parentNode) {
            audio.parentNode.removeChild(audio);
          }
        } catch (e) {
          console.warn("[TTS] Error stopping audio element:", e);
        }
        currentTTSAudioRef.current = null;
      }

      // Stop and clean up the MediaSource
      if (currentTTSMediaSourceRef.current) {
        try {
          if (currentTTSMediaSourceRef.current.readyState === 'open') {
            currentTTSMediaSourceRef.current.endOfStream();
          }
        } catch (e) {
          console.warn("[TTS] Error ending MediaSource stream:", e);
        }
        currentTTSMediaSourceRef.current = null;
      }

      // Reset TTS playing flag
      isTTSPlayingRef.current = false;

      // Clear visemes and reply text
      setSpeechMarks(null);
      setReplyText(null);
    }

    // If audioResponse and visemesData are provided, use them; otherwise fetch them
    async function playSentenceTTS(sentence, audioResponse = null, visemesData = null, isCrying = false) {
      if (!sentence.trim()) return Promise.resolve();

      // Stop any currently playing TTS audio before starting a new one
      stopCurrentTTS();

      // Ensure microphone is fully stopped while TTS audio is playing
      // Ensure microphone is fully stopped while TTS audio is playing
      if (isRecordingRef.current && isManualRecordingStop.current === false) {
        console.log("[TTS] Stopping microphone before playing TTS sentence");
        stopRecording();
      }

      isTTSPlayingRef.current = true;

      // Fetch both TTS audio and visemes in parallel if not provided
      if (!audioResponse || !visemesData) {
        const fetched = await fetchTTSAndVisemesParallel(sentence);
        audioResponse = fetched.audioResponse;
        visemesData = fetched.visemesData;
      }

      if (!audioResponse || !audioResponse.body) {
        console.error("TTS request failed for sentence:", sentence);
        return Promise.resolve();
      }

      // Use the audioResponse from parallel fetch
      const response = audioResponse;

      // Set visemes if provided
      if (visemesData && visemesData.visemes) {
        setSpeechMarks(visemesData.visemes);
      }

      // Create audio element for this sentence
      const audio = document.createElement("audio");
      // DON'T set autoplay yet - wait until we have data to avoid whistle sound
      audio.autoplay = false;
      audio.muted = true; // Mute initially to prevent any initialization sounds
      audio.volume = 0; // Also set volume to 0 as extra safety
      audio.preload = "none"; // Don't preload anything

      // Store reference to current audio element so it can be stopped if a new response arrives
      currentTTSAudioRef.current = audio;

      // Create MediaSource and attach immediately (required for sourceopen event)
      const mediaSource = new MediaSource();
      audio.src = URL.createObjectURL(mediaSource);
      currentTTSMediaSourceRef.current = mediaSource;

      // Add to DOM but keep muted until we have data
      document.body.appendChild(audio);

      return new Promise((resolve) => {
        let sourceOpenFired = false;
        let cleanup = null; // Will be defined inside sourceopen

        const sourceOpenTimeout = setTimeout(() => {
          if (!sourceOpenFired) {
            console.warn("MediaSource sourceopen event did not fire");
            // Basic cleanup if sourceopen never fires
            setTimeout(() => {
              try {
                if (audio.parentNode) {
                  audio.parentNode.removeChild(audio);
                }
              } catch (e) {
                // Ignore cleanup errors
              }
              if (currentTTSMediaSourceRef.current === mediaSource) {
                currentTTSMediaSourceRef.current = null;
              }
              resolve();
            }, 100);
          }
        }, 5000);

        mediaSource.addEventListener("sourceopen", async () => {
          sourceOpenFired = true;
          clearTimeout(sourceOpenTimeout);
          const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
          const reader = response.body.getReader();
          let queue = [];
          let processing = false;
          let streamEnded = false;

          let audioStarted = false; // Track if audio has started playing
          let firstChunkAppended = false; // Track if first chunk has been appended

          function processQueue() {
            if (processing || queue.length === 0 || sourceBuffer.updating) return;
            processing = true;
            sourceBuffer.appendBuffer(queue.shift());
            firstChunkAppended = true;
            // Don't start playback here - wait for updateend event
          }

          sourceBuffer.addEventListener("updateend", () => {
            processing = false;

            // Start playing audio ONLY after the first chunk has been fully appended and buffered
            // Wait for at least some buffered time to avoid initialization sounds
            if (!audioStarted && firstChunkAppended && sourceBuffer.buffered.length > 0) {
              const bufferedTime = sourceBuffer.buffered.end(0) - sourceBuffer.buffered.start(0);
              // Wait for at least 0.1 seconds of audio to be buffered before starting playback
              if (bufferedTime >= 0.1) {
                audioStarted = true;
                // Unmute and set volume before playing
                // Reduce volume to 50% if patient is crying
                audio.muted = false;
                audio.volume = isCrying ? 0.5 : 1.0;
                // Add a small delay to ensure buffer is ready
                setTimeout(() => {
                  audio.play().then(() => {
                    // Dispatch custom event when audio starts playing to sync lip sync
                    const timestamp = performance.now() / 1000;
                    console.log(`[TTS] Audio started playing, dispatching tts-audio-started event at ${timestamp}`);
                    window.dispatchEvent(new CustomEvent('tts-audio-started', {
                      detail: { timestamp }
                    }));
                  }).catch(err => {
                    console.warn("Error starting audio playback on updateend:", err);
                  });
                }, 50);
              }
            }

            processQueue();
            if (streamEnded && queue.length === 0 && !sourceBuffer.updating) {
              if (mediaSource.readyState === 'open') {
                mediaSource.endOfStream();
              }
              // Ensure audio is playing if it hasn't started yet (fallback)
              if (!audioStarted && audio.paused && sourceBuffer.buffered.length > 0) {
                audioStarted = true;
                audio.muted = false;
                // Reduce volume to 50% if patient is crying
                audio.volume = isCrying ? 0.5 : 1.0;
                audio.play().then(() => {
                  // Dispatch custom event when audio starts playing to sync lip sync
                  const timestamp = performance.now() / 1000;
                  console.log(`[TTS] Audio started playing (fallback), dispatching tts-audio-started event at ${timestamp}`);
                  window.dispatchEvent(new CustomEvent('tts-audio-started', {
                    detail: { timestamp }
                  }));
                }).catch(err => {
                  console.warn("Error starting audio playback after stream end:", err);
                });
              }
            }
          });

          const pump = async () => {
            try {
              const { done, value } = await reader.read();
              if (done) {
                streamEnded = true;
                if (!sourceBuffer.updating && queue.length === 0) {
                  if (mediaSource.readyState === 'open') {
                    mediaSource.endOfStream();
                  }
                }
                return;
              }
              queue.push(value.buffer);
              processQueue();
              pump();
            } catch (err) {
              console.error("Error reading TTS stream:", err);
              if (cleanup) cleanup();
            }
          };

          // Define cleanup function inside sourceopen so it has access to streamEnded, queue, sourceBuffer
          let cleanupCalled = false; // Flag to prevent multiple cleanup calls
          cleanup = () => {
            // Prevent cleanup from running multiple times
            if (cleanupCalled) {
              console.log("[TTS] Cleanup already called, skipping duplicate");
              return;
            }
            cleanupCalled = true;

            if (streamEnded && queue.length === 0 && !sourceBuffer.updating) {
              if (mediaSource.readyState === 'open') {
                mediaSource.endOfStream();
              }
            }

            isTTSPlayingRef.current = false; // [New Code]
            console.log("[TTS] isTTSPlayingRef set to FALSE"); // [New Code]

            setTimeout(() => {
              try {
                // Revoke object URL to free memory
                if (audio.src && audio.src.startsWith('blob:')) {
                  URL.revokeObjectURL(audio.src);
                }
                if (audio.parentNode) {
                  audio.parentNode.removeChild(audio);
                }
              } catch (e) {
                // Ignore cleanup errors
              }
              // Clear audio reference if this is the current audio
              if (currentTTSAudioRef.current === audio) {
                currentTTSAudioRef.current = null;
              }
              if (currentTTSMediaSourceRef.current === mediaSource) {
                currentTTSMediaSourceRef.current = null;
              }

              // Clear visemes and reply text after TTS finishes to prevent unwanted lip movement on window resize
              setSpeechMarks(null);
              setReplyText(null);

              // Restart recording after TTS finishes (using Smart Unmute Helper)
              attemptSmartUnmute();

              resolve();
            }, 100);
          };

          // Set up event listeners after cleanup is defined
          audio.addEventListener("ended", cleanup);
          audio.addEventListener("error", (e) => {
            console.error("TTS audio error:", e);
            cleanup();
          });

          pump();
        });

        // Fallback timeout
        const estimatedDuration = Math.max(3000, (sentence.split(' ').length / 150) * 60 * 1000);
        setTimeout(() => {
          if (audio.ended || audio.paused) {
            cleanup();
          } else {
            const checkInterval = setInterval(() => {
              if (audio.ended || audio.paused) {
                clearInterval(checkInterval);
                cleanup();
              }
            }, 500);
            setTimeout(() => {
              clearInterval(checkInterval);
              cleanup();
            }, estimatedDuration + 2000);
          }
        }, estimatedDuration + 5000);
      });
    }

    // [NEW] Smart Unmute Helper
    const attemptSmartUnmute = () => {
      // [SMART UNMUTE] If there are more items in the queue or streaming is active OR we are fetching audio, 
      // DO NOT RESTART MIC yet. Keep it muted for the next sentence.
      if (ttsQueueRef.current.length > 0 || isStreamingTTSRef.current || isFetchingTTSRef.current) {
        console.log("[Smart Unmute] Queue/Stream/Fetch active. Keeping mic muted. Queue:", ttsQueueRef.current.length, "Stream:", isStreamingTTSRef.current, "Fetch:", isFetchingTTSRef.current);
        return;
      }

      if (isProcessingRef.current == true && isManualRecordingStop.current === false) {
        console.log("[Smart Unmute] Conditions met. Attempting to restart recording...");

        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
          processingTimeoutRef.current = null;
        }
        setIsProcessing(false);
        isProcessingRef.current = false;

        // Clear any buffered text
        bufferRef.current = "";
        setMessage("");
        messageRef.current = "";

        // Wait to ensure TTS audio is fully clear
        setTimeout(() => {
          const ttsStillActive = isTTSPlayingRef.current;
          if (isManualRecordingStop.current === false && !ttsStillActive) {
            if (!isRecordingRef.current) {
              console.log("[Smart Unmute] Restoring isRecordingRef to true");
              isRecordingRef.current = true;
              setIsRecording(true);
            }

            if (recognitionRef.current) {
              try { recognitionRef.current.stop(); } catch (e) { }
            }

            setTimeout(() => {
              if (isRecordingRef.current && isManualRecordingStop.current === false && !isTTSPlayingRef.current) {
                bufferRef.current = "";
                setMessage("");
                messageRef.current = "";
                console.log("[Smart Unmute] Restarting recording now.");
                startRecording();
              }
            }, 200);
          }
        }, 300);
      } else {
        console.log("[Smart Unmute] Conditions NOT met. isProcessing:", isProcessingRef.current, "isManualStop:", isManualRecordingStop.current);
      }
    };

    // Process the TTS queue sequentially
    const processTTSQueue = async () => {
      if (isTTSQueuePlayingRef.current) return;

      if (ttsQueueRef.current.length === 0) {
        return;
      }

      isTTSQueuePlayingRef.current = true;
      const { text, audioPromise, isCrying } = ttsQueueRef.current.shift();

      try {
        console.log(`[TTS Queue] Processing: "${text}"`);

        // [FIX] Mark as fetching so attemptSmartUnmute doesn't trigger during the gap
        isFetchingTTSRef.current = true;

        // Wait for audio to be fetched
        const { audioResponse, visemesData } = await audioPromise;

        isFetchingTTSRef.current = false; // Done fetching

        if (audioResponse && audioResponse.body) {
          // wait for previous audio to fully clean up if needed
          await playSentenceTTS(text, audioResponse, visemesData, isCrying);
        } else {
          if (!isMuted) {
            console.warn("[TTS Queue] Audio fetch failed or empty body for:", text);
          }
          // [FIX] If we skip playing, we MUST attempt unmute, otherwise mic stays muted from previous item
          attemptSmartUnmute();
        }
      } catch (e) {
        console.error("[TTS Queue] Error processing item:", e);
        isFetchingTTSRef.current = false; // Ensure flag is cleared on error
        // [FIX] Error case also needs unmute check
        attemptSmartUnmute();
      } finally {
        isTTSQueuePlayingRef.current = false;
        // Process next item
        processTTSQueue();
      }
    };

    const handleReplyChunk = (data) => {
      const chunk = data.chunk;
      if (!chunk) return;

      isStreamingTTSRef.current = true;

      // Append to accumulator
      messageAccumulatorRef.current += chunk;

      // Append to UI message immediately for typing effect
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.role === patientNameRef.current) {
          // Update existing message
          const newContent = lastMsg.content + chunk;
          return [...prev.slice(0, -1), { ...lastMsg, content: newContent }];
        } else {
          // New message
          return [...prev, { role: patientNameRef.current, content: chunk, timestamp: new Date().toISOString() }];
        }
      });

      // Sentence detection logic
      // We looking for sentence terminators followed by a Capital letter (start of next sentence).
      // This avoids splitting on abbreviations (e.g. "Dr.") or mid-sentence pauses ("... like").
      // We DO NOT match end of string ($) here, because we want to wait for context to ensure it's a real sentence end.
      // The final sentence will always be caught by handleReplyComplete as the remainder.
      let text = messageAccumulatorRef.current;

      const sentenceRegex = /([.?!]+)(?=\s+[A-Z])/g;

      let match;
      let lastIndex = 0;

      // Find all sentences
      while ((match = sentenceRegex.exec(text)) !== null) {
        const endIndex = match.index + match[1].length;
        const sentence = text.substring(lastIndex, endIndex).trim();

        if (sentence) {
          // Determine if we should trigger TTS for this sentence
          // Fetch in parallel
          console.log(`[Streaming TTS] Detected sentence: "${sentence}" (endIndex: ${endIndex})`);
          const audioPromise = fetchTTSAndVisemesParallel(sentence, isCrying);

          ttsQueueRef.current.push({
            text: sentence,
            audioPromise: audioPromise,
            isCrying: isCrying
          });

          processTTSQueue();
        }
        lastIndex = endIndex;
      }

      // Keep the remainder in accumulator
      messageAccumulatorRef.current = text.substring(lastIndex);
      if (lastIndex > 0) {
        console.log(`[Streaming TTS] New accumulator remainder: "${messageAccumulatorRef.current}"`);
      }
    };

    const speakReply = async (text, isCryingFlag = false) => {
      const trimmed = text?.trim();
      if (!trimmed) {
        console.log("[speakReply] Empty or null text, skipping TTS");
        return;
      }

      // Log short responses to help debug
      if (trimmed.length <= 5) {
        console.log(`[speakReply] Processing short response: "${trimmed}"`);
      }

      if (isCryingFlag) {
        try {
          await playCryingSound();
        } catch (err) {
          console.error("[Crying Sound] Unable to play crying audio:", err);
        }
      }

      try {
        console.log(`[speakReply] Fetching TTS for: "${trimmed}"`);
        const { audioResponse, visemesData } = await fetchTTSAndVisemesParallel(trimmed, isCryingFlag);
        if (!audioResponse || !audioResponse.body) {
          if (!isMutedRef.current) {
            console.error("[speakReply] TTS audio request failed - no response body");
          }
          // [FIX] Even if no audio (mute or error), we MUST attempt to unmute the mic
          // otherwise the user is stuck in silence/processing state.
          console.log("[speakReply] No audio to play, attempting smart unmute...");
          setTimeout(() => attemptSmartUnmute(), 100);
          return;
        }
        console.log(`[speakReply] Playing TTS for: "${trimmed}"`);
        await playSentenceTTS(trimmed, audioResponse, visemesData, isCryingFlag);
      } catch (err) {
        console.error("[speakReply] Error playing TTS:", err);
      } finally {
        if (isCryingFlag) {
          setIsCrying(false);
        }
      }
    };

    const handleReplyComplete = (data) => {
      console.log("[handleReplyComplete] Received reply_complete event:", data);

      // Stop any currently playing TTS audio immediately when a new response arrives
      // BUT if we are streaming, we should NOT stop, as the queue is playing chunks
      if (!isStreamingTTSRef.current && isTTSPlayingRef.current) {
        console.log("[handleReplyComplete] Stopping current TTS (non-streaming mode)");
        stopCurrentTTS();
      } else if (isStreamingTTSRef.current) {
        console.log("[handleReplyComplete] Streaming active - NOT stopping TTS");
      }

      // Backend now sends full reply in one go
      let fullReply = data.reply || "";
      console.log("[handleReplyComplete] Initial fullReply:", fullReply);

      // Parse JSON to extract the "reply" field if the response is JSON
      try {
        const jsonMatch = fullReply.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          // Try different possible keys
          fullReply = parsed.reply || parsed.response || parsed.patient_response ||
            (typeof parsed.patient_response === 'object' ? JSON.stringify(parsed.patient_response) : null) ||
            fullReply;
          console.log("[handleReplyComplete] After JSON parsing:", fullReply);
        }
      } catch (e) {
        // If parsing fails, try to extract reply field using regex
        const replyMatch = fullReply.match(/"reply"\s*:\s*"([^"]*)"/);
        if (replyMatch) {
          fullReply = replyMatch[1];
          console.log("[handleReplyComplete] After regex extraction (reply):", fullReply);
        } else {
          const responseMatch = fullReply.match(/"response"\s*:\s*"([^"]*)"/);
          if (responseMatch) {
            fullReply = responseMatch[1];
            console.log("[handleReplyComplete] After regex extraction (response):", fullReply);
          }
        }
      }

      // Ensure we have a valid reply before proceeding
      if (!fullReply || !fullReply.trim()) {
        console.warn("[handleReplyComplete] Empty or invalid reply after parsing, skipping TTS");
        return;
      }

      // Check if patient is crying and play crying sound effect
      // Only play if explicitly set to true (not just truthy)
      const is_crying = data.is_crying === true || (data.emotion_state && data.emotion_state.crying === true);
      setIsCrying(is_crying); // Update crying state for avatar tear effect

      // Update meter values
      if (data.breadcrumb_level) {
        setBreadcrumbLevel(data.breadcrumb_level);
      }
      if (data.emotion_json) {
        if (data.emotion_json.mood) {
          setMood(data.emotion_json.mood);
        }
        if (data.emotion_json.intensity !== undefined) {
          setIntensity(data.emotion_json.intensity);
        }
      }
      if (data.trust !== undefined) {
        setTrust(data.trust);
      }

      // Update insight state if provided by backend
      if (data.doctor_style_state) {
        setDoctorStyleState(data.doctor_style_state);
      }
      if (data.therapeutic_alliance) {
        setTherapeuticAlliance(data.therapeutic_alliance);
      }
      if (data.trigger_system) {
        setTriggerSystem(data.trigger_system);
      }
      if (data.coping_mechanisms) {
        setCopingMechanisms(data.coping_mechanisms);
      }
      if (data.behavioral_patterns) {
        setBehavioralPatterns(data.behavioral_patterns);
      }
      if (data.progress_tracker) {
        setProgressTracker(data.progress_tracker);
      }
      if (data.session_metrics) {
        setSessionMetrics(data.session_metrics);
      }

      const displayReply = [fullReply].filter(Boolean).join(" ").trim();

      console.log("[handleReplyComplete] Calling speakReply with:", fullReply, "is_crying:", is_crying);

      if (isStreamingTTSRef.current) {
        console.log("[handleReplyComplete] Streaming was active. Processing remainder.");
        // Process remaining text in accumulator
        const remainder = messageAccumulatorRef.current.trim();
        if (remainder) {
          console.log(`[Streaming TTS] Processing remainder (final): "${remainder}"`);
          const audioPromise = fetchTTSAndVisemesParallel(remainder, is_crying);
          ttsQueueRef.current.push({
            text: remainder,
            audioPromise: audioPromise,
            isCrying: is_crying
          });
          processTTSQueue();
        } else {
          console.log("[Streaming TTS] No remainder to process.");
        }
        // Reset states
        messageAccumulatorRef.current = "";
        isStreamingTTSRef.current = false;

        // If backend profile guardrails corrected the reply (e.g. gender/spouse/children),
        // update the last displayed patient message so the therapist sees the corrected text.
        if (data.profile_correction_applied) {
          console.log("[handleReplyComplete] Profile correction applied. Updating last patient message with corrected reply.");
          setMessages((prev) => {
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.role === patientNameRef.current) {
              // Ensure timestamp is preserved or updated if provided
              const timestamp = data.timestamp || lastMsg.timestamp || new Date().toISOString();
              return [...prev.slice(0, -1), { role: patientNameRef.current, content: displayReply, timestamp: timestamp }];
            }
            // Fallback if no last message
            const timestamp = data.timestamp || new Date().toISOString();
            return [...prev, { role: patientNameRef.current, content: displayReply, timestamp: timestamp }];
          });
        } else {
          // Otherwise preserve streamed text so audio and text stay aligned.
          console.log("[handleReplyComplete] No profile correction. Preserving streamed text for consistency with audio.");
        }
      } else {
        // Fallback for non-streaming: Append NEW message
        const timestamp = data.timestamp || new Date().toISOString();
        setMessages((prev) => [...prev, { role: patientNameRef.current, content: displayReply, timestamp: timestamp }]);
        speakReply(fullReply, is_crying);
      }
    };
    s.on("reply_complete", handleReplyComplete);
    s.on("reply_chunk", handleReplyChunk);

    // Add error handler to reset processing state if socket errors occur
    const handleError = (error) => {
      console.error("Socket error:", error);
      if (isProcessingRef.current && isManualRecordingStop.current === false) {
        // Clear any pending timeout
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
          processingTimeoutRef.current = null;
        }
        setIsProcessing(false);
        isProcessingRef.current = false;
        setIsRecording(true);
        startRecording();
      }
    };
    s.on("error", handleError);

    const handleConnectError = (error) => {
      console.error("Connection Error:", error);
    };
    s.on("connect_error", handleConnectError);

    const handleConnect = () => {
      console.log("Socket connected/reconnected");
      if (messageRef.current) {
        s.emit("message", {
          message: messageRef.current,
          consultation_id: state.consultation_id,
          patient_id: state.patient_profile.id,
          doctor_id,
        });
      }
    };
    s.on("connect", handleConnect);

    // Cleanup function - properly disconnect and remove all listeners
    return () => {
      console.log("[Socket] Cleaning up Socket.IO connection and removing all listeners");
      isSettingUpSocketRef.current = false; // Reset setup flag on cleanup
      if (socketRef.current) {
        // Remove all event listeners to prevent memory leaks
        socketRef.current.off("status", handleStatus);
        socketRef.current.off("reply_complete", handleReplyComplete);
        socketRef.current.off("reply_chunk", handleReplyChunk);
        socketRef.current.off("error", handleError);
        socketRef.current.off("connect_error", handleConnectError);
        socketRef.current.off("connect", handleConnect);
        // Also remove all listeners as a safety measure
        socketRef.current.removeAllListeners();
        // Disconnect the socket
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
    };
    // Only depend on IDs to prevent infinite loops - use refs for state and patient_name
  }, [patient_profile?.id, consultation_id, doctor_id]);

  // Send text message
  const sendMessage = () => {
    let messageToProcess = message || messageRef.current;

    if (lowBalance == true) {
      alert("your balance is low. Please recharge.");
      return;
    }
    const currentSocket = socketRef.current || socket;

    if (!messageToProcess || !currentSocket || currentSocket.disconnected) {
      console.warn("Socket not ready, message skipped:", messageToProcess);
      // Reset processing state if message can't be sent (only if user hasn't manually muted)
      if (isProcessingRef.current && isManualRecordingStop.current === false) {
        setIsProcessing(false);
        isProcessingRef.current = false;
        setIsRecording(true);
        startRecording();
      }
      return;
    }

    const index = ignoredWords.findIndex(item =>
      item.toLowerCase() === messageToProcess.toLowerCase()
    );
    if (index == -1) {
      currentSocket.emit("message", { message: messageToProcess, consultation_id: state.consultation_id, patient_id: state.patient_profile.id, doctor_id: doctor_id });
      setMessages((prev) => [...prev, { role: "student", content: messageToProcess, timestamp: new Date().toISOString() }]);
    }
    setMessage("");
    messageRef.current = "";
  };

  // 🎤 Start voice recording
  const startRecording = async () => {
    // Clear any previous conflict message when starting recording
    setMicConflictMessage(null);
    console.log("[startRecording] Called, isManualRecordingStop:", isManualRecordingStop.current, "isTTSPlaying:", isTTSPlayingRef.current);

    // Don't start recording if user has manually muted
    if (isManualRecordingStop.current === true) {
      console.log("Recording not started: User has manually muted the mic");
      return;
    }

    // Don't start recording if TTS is active - wait for it to finish
    const ttsActive = isTTSPlayingRef.current;
    if (ttsActive) {
      console.log("Recording not started: TTS is still active, waiting...");
      // Retry almost immediately once TTS stops
      setTimeout(() => {
        if (isManualRecordingStop.current === false) {
          startRecording();
        }
      }, 0);
      return;
    }

    // Additional safety: Wait a bit more to ensure TTS audio has fully stopped
    // This prevents picking up any residual TTS audio
    setTimeout(() => {
      // Double-check TTS is still not active
      const ttsStillActive = isTTSPlayingRef.current;
      if (ttsStillActive || isManualRecordingStop.current === true) {
        console.log("[startRecording] Aborted: TTS still active or manual stop is true");
        return;
      }

      // Clear any buffered text that might have accumulated from TTS transcription
      bufferRef.current = "";
      setMessage("");
      messageRef.current = "";

      // Now actually start the recording
      if (lowBalance == true) {
        alert("your balance is low. Please recharge.");
        return;
      }

      try {
        console.log("[startRecording] Starting browser recording...");
        // Show loading state immediately for better UX in production
        setIsRecording(true); // Set optimistically to show loading state
        isRecordingRef.current = true;

        //backendAudioTranscript();
        startBrowserRecording().catch((err) => {
          console.error("[startRecording] startBrowserRecording promise rejected:", err);
          // Error handling is already done in startBrowserRecording, but log here for visibility
          setIsRecording(false);
          isRecordingRef.current = false;
        });
      } catch (err) {
        console.error("[startRecording] Synchronous error:", err);
        console.error("[startRecording] Error name:", err.name);
        console.error("[startRecording] Error message:", err.message);
        setIsRecording(false);
        isRecordingRef.current = false;
        alert("Failed to start microphone. Please check your browser permissions and try again.");
      }
    }, 500);
  };

  const backendAudioTranscript = async () => {
    // ✅ Backend-based audio recording (your existing flow)
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    isRecordingRef.current = true;
    micStreamRef.current = stream;

    let customGainNode = startSilenceDetection(stream);
    const audioCtx = audioContextRef.current;
    const destination = audioCtx.createMediaStreamDestination();
    customGainNode.connect(destination);

    const mediaRecorder = new MediaRecorder(destination.stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });

      // ✅ check if there was voice
      if (maxRmsRef.current < silenceDetection) {
        console.log("No speech detected, skipping transcription.");
        maxRmsRef.current = 0;
        //Nothing detected so start recording again (only if user hasn't manually muted)
        if (isProcessingRef.current == true && isManualRecordingStop.current === false) {
          setIsProcessing(false);
          isProcessingRef.current = false;
          setIsRecording(true);
          startRecording();
        }
        return; // don't send to backend
      }
      maxRmsRef.current = 0; // reset for next recording

      // ✅ Save locally (prompt download)
      if (downloadRecording == true) {
        const audioUrl = URL.createObjectURL(audioBlob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = audioUrl;
        a.download = "recording.webm"; // file name for user
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(audioUrl);
      }
      const formData = new FormData();
      formData.append("file", audioBlob, "audio.webm");

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transcribe/stream`, {
          method: "POST",
          body: formData,
          headers: get_request_header(false),
        });
        const data = await response.json();
        if (data.text) {
          setMessage(data.text);
          messageRef.current = data.text;
          sendMessage();
        } else {
          // No text received, reset processing state (only if user hasn't manually muted)
          if (isProcessingRef.current && isManualRecordingStop.current === false) {
            // Clear any pending timeout
            if (processingTimeoutRef.current) {
              clearTimeout(processingTimeoutRef.current);
              processingTimeoutRef.current = null;
            }
            setIsProcessing(false);
            isProcessingRef.current = false;
            setIsRecording(true);
            startRecording();
          }
        }
      } catch (err) {
        console.error("Transcription failed:", err);
        // Reset processing state on error (only if user hasn't manually muted)
        if (isProcessingRef.current && isManualRecordingStop.current === false) {
          // Clear any pending timeout
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
            processingTimeoutRef.current = null;
          }
          setIsProcessing(false);
          isProcessingRef.current = false;
          setIsRecording(true);
          startRecording();
        }
      }
    };

    mediaRecorder.start();
    setIsRecording(true);
    drawWaveform(); // start drawing
  }

  // Add this new function inside your component, alongside startSilenceDetection
  const startVolumeMonitoringForBrowser = (stream) => {
    // Ensure an AudioContext exists
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const audioCtx = audioContextRef.current;
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;

    // Track nodes so they can be disconnected on stop
    audioNodesRef.current.source = source;
    audioNodesRef.current.analyser = analyser;

    // 3. Connect nodes: Source (Mic) -> Analyser (for volume check)
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.fftSize);

    const monitorVolume = () => {
      // Stop monitoring if recording has been stopped
      if (!isRecordingRef.current) {
        return;
      }
      analyser.getByteTimeDomainData(dataArray);

      // Compute RMS (Volume Level)
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        let v = (dataArray[i] - 128) / 128.0;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length) * 100; // 0–100 scale

      // 4. Update the ref used by drawWaveform()
      rmsRef.current = rms;

      // 5. Continue the loop
      animationFrameRef.current = requestAnimationFrame(monitorVolume);
    };

    monitorVolume();

  };

  //Browser based recording
  const startBrowserRecording = async () => {
    console.log("[startBrowserRecording] Starting...");

    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      alert("Speech Recognition not supported in this browser.");
      return;
    }

    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Microphone access is not available in this browser. Please use a modern browser like Chrome, Firefox, or Edge.");
      return;
    }

    // Check if we're in a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      alert("Microphone access requires a secure connection (HTTPS) or localhost. Please access this page via HTTPS or localhost.");
      return;
    }

    try {
      // CRITICAL: Stop and clean up any existing recognition FIRST to prevent "already started" errors
      if (recognitionRef.current) {
        console.log("[startBrowserRecording] Stopping existing recognition before starting new one");
        try {
          // Remove all handlers FIRST to prevent restart logic from firing
          recognitionRef.current.onend = null;
          recognitionRef.current.onresult = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onstart = null;
          // Clear any pending restart timeouts BEFORE stopping
          recognitionRestartTimeoutsRef.current.forEach(timeoutId => {
            clearTimeout(timeoutId);
          });
          recognitionRestartTimeoutsRef.current = [];
          // Stop the recognition
          try {
            recognitionRef.current.stop();
          } catch (stopErr) {
            // Ignore stop errors - recognition might already be stopped
          }
          // Also try to abort if available
          if (typeof recognitionRef.current.abort === 'function') {
            try {
              recognitionRef.current.abort();
            } catch (abortErr) {
              // Ignore abort errors
            }
          }
        } catch (stopErr) {
          console.warn("[startBrowserRecording] Error stopping existing recognition:", stopErr);
        }
        // Clear the reference
        recognitionRef.current = null;
        // Reset the starting flag
        isRecognitionStartingRef.current = false;
        // Reset restart counters for new session
        recognitionRestartCountRef.current = 0;
        lastRecognitionEndTimeRef.current = 0;
        recognitionStartTimeRef.current = 0;
        // Wait longer to ensure recognition has fully stopped (production may need more time)
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Check permissions if available (not all browsers support this)
      // Add timeout to permission check to prevent hanging in production
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionQueryPromise = navigator.permissions.query({ name: 'microphone' });
          const permissionTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Permission query timeout')), 2000); // 2 second timeout
          });

          try {
            const permissionStatus = await Promise.race([permissionQueryPromise, permissionTimeoutPromise]);
            console.log("[startBrowserRecording] Microphone permission status:", permissionStatus.state);
            if (permissionStatus.state === 'denied') {
              setShowMicrophonePermissionPopup(true);
              return;
            }
          } catch (timeoutErr) {
            // Permission query timed out - continue anyway, getUserMedia will handle it
            console.warn("[startBrowserRecording] Permission query timed out, continuing with getUserMedia:", timeoutErr);
          }
        } catch (permErr) {
          console.warn("[startBrowserRecording] Could not check permissions:", permErr);
          // Continue anyway - some browsers don't support permission query
        }
      }

      // Always clean up existing stream and get a fresh one
      // Reusing streams can cause recognition to fail silently in production
      let stream = null;
      if (micStreamRef.current) {
        console.log("[startBrowserRecording] Cleaning up existing stream to get fresh one...");
        try {
          const tracks = micStreamRef.current.getTracks();
          tracks.forEach(track => {
            console.log(`[startBrowserRecording] Stopping track: ${track.id}, state: ${track.readyState}, muted: ${track.muted}`);
            track.stop();
            track.enabled = false;
          });
        } catch (cleanupErr) {
          console.warn("[startBrowserRecording] Error cleaning up existing stream:", cleanupErr);
        }
        micStreamRef.current = null;
        // Wait a moment after cleanup before requesting new stream
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Ensure AudioContext is running when starting
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        try {
          await audioContextRef.current.resume();
          console.log("[startBrowserRecording] AudioContext resumed");
        } catch (e) {
          console.warn('[startBrowserRecording] AudioContext resume failed:', e);
        }
      }
      // 🛑 STEP 1: Get the audio stream with constraints (AGC/NS)
      // Only request new stream if we don't have an active one
      if (!stream) {
        console.log("[startBrowserRecording] Requesting microphone access...");
        console.log("[startBrowserRecording] Permission already granted, calling getUserMedia...");
        const startTime = Date.now();

        try {
          // Create a helper function to try getUserMedia with fallback
          const tryGetUserMedia = async () => {
            console.log("[startBrowserRecording] Attempting getUserMedia with full constraints...");
            const attemptStartTime = Date.now();
            try {
              // Try with full constraints first
              const result = await navigator.mediaDevices.getUserMedia({
                audio: {
                  // Request the browser/OS to handle volume stabilization
                  autoGainControl: true,
                  // Request the browser/OS to handle background noise filtering
                  noiseSuppression: true,
                  // We don't want the browser to filter out silence aggressively
                  echoCancellation: false,
                },
              });
              const attemptDuration = Date.now() - attemptStartTime;
              console.log(`[startBrowserRecording] getUserMedia with full constraints succeeded in ${attemptDuration}ms`);
              return result;
            } catch (firstError) {
              const attemptDuration = Date.now() - attemptStartTime;
              console.warn(`[startBrowserRecording] Failed with full constraints after ${attemptDuration}ms:`, firstError);
              // If complex constraints fail, try with simpler constraints
              // This helps in production where complex constraints might cause delays or be unsupported
              console.log("[startBrowserRecording] Attempting getUserMedia with simple constraints...");
              const fallbackStartTime = Date.now();
              try {
                const result = await navigator.mediaDevices.getUserMedia({ audio: true });
                const fallbackDuration = Date.now() - fallbackStartTime;
                console.log(`[startBrowserRecording] getUserMedia with simple constraints succeeded in ${fallbackDuration}ms`);
                return result;
              } catch (secondError) {
                const fallbackDuration = Date.now() - fallbackStartTime;
                console.error(`[startBrowserRecording] Both constraint attempts failed. Full: ${attemptDuration}ms, Simple: ${fallbackDuration}ms`, { firstError, secondError });
                // If simple constraints also fail, throw the original error
                throw firstError;
              }
            }
          };

          // Create a timeout promise that rejects after 15 seconds (longer for production)
          // Production environments may have additional security checks causing delays
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              const totalDuration = Date.now() - startTime;
              reject(new Error(`getUserMedia timeout after ${totalDuration}ms: Microphone access request took too long. This may be due to browser security checks. Please try again.`));
            }, 15000); // Increased to 15 seconds for production
          });

          // Race between getUserMedia (with fallback) and timeout
          stream = await Promise.race([tryGetUserMedia(), timeoutPromise]);
          const totalDuration = Date.now() - startTime;
          console.log(`[startBrowserRecording] Microphone access granted successfully in ${totalDuration}ms`);
        } catch (error) {
          const totalDuration = Date.now() - startTime;
          console.error(`[startBrowserRecording] getUserMedia failed after ${totalDuration}ms:`, error);
          console.error("[startBrowserRecording] Error name:", error.name);
          console.error("[startBrowserRecording] Error message:", error.message);

          // Reset recording state on error
          setIsRecording(false);
          isRecordingRef.current = false;

          // Handle permission denied errors
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            setShowMicrophonePermissionPopup(true);
            return;
          }

          // Handle timeout errors
          if (error.message && error.message.includes('timeout')) {
            alert("Microphone access request timed out. Please try again or refresh the page.");
            return;
          }

          // Handle device not found errors
          if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
            alert("No microphone found. Please connect a microphone and try again.");
            return;
          }

          // Handle other errors
          alert(`Failed to access microphone: ${error.message || error.name}. Please check your browser permissions and try again.`);
          return;
        }
      }

      // Ensure we have a stream before proceeding
      if (!stream) {
        console.error("[startBrowserRecording] No stream available after getUserMedia attempt");
        setIsRecording(false);
        isRecordingRef.current = false;
        return;
      }

      // Save the stream reference so we can stop it later (in stopRecording)
      micStreamRef.current = stream;
      // Mark recording active before starting visualizers so they don't exit early
      isRecordingRef.current = true;
      setIsRecording(true);

      startVolumeMonitoringForBrowser(stream);
      drawWaveform(); // Start the canvas animation

      // 🛑 STEP 2: Initialize and configure Speech Recognition
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = patient_lang;

      // *** NOTE: You cannot directly feed a MediaStream to the Web Speech API.
      // The API internally requests access. The main benefit of the above
      // 'getUserMedia' call is to ensure the browser has already initialized
      // the microphone with these settings before the 'recognition.start()' call. ***

      recognition.onresult = (event) => {
        console.log("[Recognition] onresult called, results count:", event.results.length, "resultIndex:", event.resultIndex);
        // CRITICAL: Ignore recognition results if TTS is playing
        // This prevents TTS audio from being transcribed into the message textbox
        const ttsActive = isTTSPlayingRef.current;
        if (ttsActive) {
          console.log("Ignoring recognition result - TTS in progress");
          // Also clear any buffered text to prevent it from being sent
          bufferRef.current = "";
          setMessage("");
          messageRef.current = "";
          if (ttsActive && recognitionRef.current) {
            try {
              recognitionRef.current.stop();
            } catch (e) {
              console.warn("Error stopping recognition:", e);
            }
          }
          return;
        }

        let finalTranscript = "";
        let interimTranscript = "";

        // SIMPLE APPROACH: Process results and accumulate final/interim separately
        // This is the working pattern from before
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const result = event.results[i];
          // Double-check TTS is not active before processing any results (including interim)
          const ttsStillActive = isTTSPlayingRef.current;
          if (ttsStillActive) {
            console.log("TTS became active during result processing, ignoring");
            bufferRef.current = "";
            setMessage("");
            messageRef.current = "";
            return;
          }

          const transcript = result[0].transcript;
          if (result.isFinal) {
            // Accumulate final results
            finalTranscript += transcript + " ";
            // Clear pending interim since we got a final result
            pendingInterimRef.current = "";
          } else {
            // Handle interim results - these are partial words in progress
            interimTranscript += transcript + " ";
            // Store the latest interim result in case recognition is aborted
            const currentInterim = transcript.trim();
            if (!pendingInterimRef.current || currentInterim.length > pendingInterimRef.current.length) {
              pendingInterimRef.current = currentInterim;
            }
          }
        }

        // Add final results to buffer (simple append - no complex logic)
        if (finalTranscript) {
          // [FIX] Clean leading punctuation (commas, periods) that sometimes appear from STT
          let cleanTranscript = finalTranscript.trim();
          if (cleanTranscript.startsWith(',')) cleanTranscript = cleanTranscript.substring(1).trim();
          if (cleanTranscript.startsWith('.')) cleanTranscript = cleanTranscript.substring(1).trim();

          if (cleanTranscript) {
            bufferRef.current += cleanTranscript + " ";
            console.log("[Recognition] Added final transcript to buffer:", cleanTranscript, "Buffer now:", bufferRef.current);
          }
        }

        // Only update message if TTS is not active
        const ttsStillActive = isTTSPlayingRef.current;
        if (ttsStillActive) {
          bufferRef.current = "";
          setMessage("");
          messageRef.current = "";
          return;
        }

        // Update UI: show buffer + interim results
        if (finalTranscript) {
          // Update messageRef to buffer (final results only)
          messageRef.current = bufferRef.current;
          setMessage(bufferRef.current);
          // Reset silence timer when final transcript is received
          monitorBrowserIdealRecordingtimer();
        }

        // Show interim results for real-time feedback
        if (interimTranscript) {
          const displayText = bufferRef.current + interimTranscript;
          // Show buffer + interim in UI, but messageRef only has final results
          setMessage(displayText);
          // CRITICAL: Reset timer on interim results too - user is still speaking!
          // This prevents auto-submit while user is actively talking
          monitorBrowserIdealRecordingtimer();
          console.log("[Recognition] Showing interim transcript (UI only):", interimTranscript.trim(), "Display:", displayText);
        } else if (finalTranscript) {
          // If we only had final results, make sure message is updated
          setMessage(bufferRef.current);
          messageRef.current = bufferRef.current;
        }
      };

      recognition.onstart = () => {
        // Recognition has successfully started, reset the flag and restart counter
        isRecognitionStartingRef.current = false;
        recognitionStartTimeRef.current = Date.now();
        // Reset restart count if recognition has been active for more than 2 seconds
        if (recognitionStartTimeRef.current - lastRecognitionEndTimeRef.current > 2000) {
          recognitionRestartCountRef.current = 0;
        }
        // CRITICAL: When recognition restarts, DON'T reset lastProcessedFinalTextRef
        // We need to keep track of what we've processed to extract only new words
        // The buffer should still contain previously finalized words
        console.log("[Recognition] Started successfully. Buffer:", bufferRef.current, "Last processed:", lastProcessedFinalTextRef.current);
      };

      recognition.onend = () => {
        // Reset the starting flag when recognition ends
        isRecognitionStartingRef.current = false;
        const now = Date.now();
        lastRecognitionEndTimeRef.current = now;

        // Check if recognition ended too quickly after starting (possible infinite loop)
        const timeSinceStart = now - recognitionStartTimeRef.current;

        // Check BEFORE incrementing to catch the 5th rapid restart
        if (timeSinceStart < 1000 && recognitionRestartCountRef.current >= 4) {
          // We've already had 4 rapid restarts, this would be the 5th - stop the loop
          console.error(`Too many rapid recognition restarts detected (${recognitionRestartCountRef.current + 1}). Stopping to prevent infinite loop.`);
          setIsRecording(false);
          isRecordingRef.current = false;
          recognitionRestartCountRef.current = 0; // Reset for next attempt
          alert("Speech recognition encountered an error. Please try unmuting the microphone again.");
          return;
        }

        if (timeSinceStart < 1000 && recognitionRestartCountRef.current > 0) {
          // Recognition ended within 1 second of starting - likely an error loop
          recognitionRestartCountRef.current++;
          console.warn(`Recognition ended too quickly (${timeSinceStart}ms). Restart count: ${recognitionRestartCountRef.current}`);
        } else {
          // Normal end (more than 1 second), reset counter and increment
          if (timeSinceStart >= 1000) {
            recognitionRestartCountRef.current = 0; // Reset on successful run
          }
          recognitionRestartCountRef.current++;
        }

        // Only restart if recording is active AND user hasn't manually muted AND TTS is not playing
        const ttsActive = isTTSPlayingRef.current;
        if (isRecordingRef.current === true && isManualRecordingStop.current === false && !ttsActive) {
          // Calculate delay based on restart count (exponential backoff for rapid failures)
          const baseDelay = 500;
          const maxDelay = 3000;
          const delay = Math.min(baseDelay * Math.pow(1.5, Math.min(recognitionRestartCountRef.current - 1, 5)), maxDelay);

          console.log(`Recognition ended (e.g., due to silence). Restarting in ${delay}ms... (attempt ${recognitionRestartCountRef.current})`);

          if (isRecordingRef.current === true && !isRecognitionStartingRef.current && recognitionRef.current) {
            // Schedule restart with delay to prevent rapid restarts
            const retryId = setTimeout(() => {
              // Double-check conditions before restarting
              if (isRecordingRef.current === true && !isRecognitionStartingRef.current && recognitionRef.current && !isTTSPlayingRef.current) {
                try {
                  isRecognitionStartingRef.current = true;
                  recognitionRef.current.start();
                  console.log(`Recognition restart attempt ${recognitionRestartCountRef.current}`);
                } catch (err) {
                  console.warn("Failed to restart recognition:", err);
                  isRecognitionStartingRef.current = false;
                  recognitionRestartCountRef.current++;

                  // If restart fails, try once more after a longer delay
                  if (recognitionRestartCountRef.current <= 3) {
                    const retryId2 = setTimeout(() => {
                      if (isRecordingRef.current === true && !isRecognitionStartingRef.current && recognitionRef.current) {
                        try {
                          isRecognitionStartingRef.current = true;
                          recognitionRef.current.start();
                        } catch (retryErr) {
                          console.error("Failed to restart recognition on retry:", retryErr);
                          isRecognitionStartingRef.current = false;
                        }
                      }
                      recognitionRestartTimeoutsRef.current = recognitionRestartTimeoutsRef.current.filter(id => id !== retryId2);
                    }, delay * 2);
                    recognitionRestartTimeoutsRef.current.push(retryId2);
                  }
                }
              }
              recognitionRestartTimeoutsRef.current = recognitionRestartTimeoutsRef.current.filter(id => id !== retryId);
            }, delay);
            recognitionRestartTimeoutsRef.current.push(retryId);
          }
        } else {
          console.log("Recognition ended. Not restarting because isRecordingRef is false, user has manually muted, or TTS is playing/queued.");
          // Reset restart count when we're not restarting
          recognitionRestartCountRef.current = 0;
        }
      };

      recognition.onerror = (event) => {
        // Reset the starting flag on any error
        isRecognitionStartingRef.current = false;

        console.log(`[Recognition] onerror called: ${event.error}`);

        // Check for microphone conflict errors - another tab or application is using the mic
        if (event.error === 'service-not-allowed' || event.error === 'aborted' || event.error === 'not-allowed') {
          // Check if this is a conflict (not just a normal abort from our own stop())
          // If we're still trying to record, it's likely a conflict
          if (isRecordingRef.current || isRecording) {
            console.error("[Recognition] Microphone conflict detected:", event.error);

            // Stop the current recognition session immediately
            try {
              recognition.stop();
            } catch (stopErr) {
              // Ignore errors from stopping
            }

            setIsRecording(false);
            isRecordingRef.current = false;

            // Set the conflict message for the user
            isManualRecordingStop.current = true;
            setMicConflictMessage("Microphone conflict detected: Another browser tab or application is using your microphone. Please close the other recording session before continuing.");
            return;
          }
        }

        // Check if the error is "aborted" (but not a conflict)
        if (event.error === 'aborted') {
          // This is the expected behavior when we manually call recognition.stop().
          // We can safely ignore it to prevent the console spam.
          // However, if we have pending interim results, preserve them for next recognition
          if (pendingInterimRef.current) {
            console.log("[Recognition] Recognition aborted with pending interim:", pendingInterimRef.current, "- will check if next recognition continues it");
          }
          return;
        }

        if (event.error === 'no-speech') {
          console.log("No Speech recognised, will restart via onend handler.");
          // Don't manually restart here - let onend handle it
          // The onend handler will restart if isRecordingRef is still true
          return;
        }

        // For network errors or service unavailable, increment restart count
        if (event.error === 'network') {
          recognitionRestartCountRef.current++;
          console.warn(`Recognition error: ${event.error}, restart count: ${recognitionRestartCountRef.current}`);
        }

        // Log all other actual errors
        console.error("Speech recognition error:", event.error, "Restart count:", recognitionRestartCountRef.current);
      };

      // 🛑 STEP 3: Start the recognition service
      try {
        console.log("[startBrowserRecording] Starting speech recognition...");
        isRecognitionStartingRef.current = true;
        recognition.start();
        console.log("[startBrowserRecording] Speech recognition start() called");
        // Flag will be reset in onstart handler when recognition actually starts
      } catch (err) {
        console.error("[startBrowserRecording] Failed to start recognition:", err);
        console.error("[startBrowserRecording] Recognition error name:", err.name);
        console.error("[startBrowserRecording] Recognition error message:", err.message);
        isRecognitionStartingRef.current = false;
        // Reset recording state if recognition fails to start
        setIsRecording(false);
        isRecordingRef.current = false;
        // Stop the stream if recognition fails
        if (micStreamRef.current) {
          try {
            micStreamRef.current.getTracks().forEach(track => {
              track.stop();
              track.enabled = false;
            });
          } catch (cleanupErr) {
            console.warn("[startBrowserRecording] Error cleaning up stream after recognition failure:", cleanupErr);
          }
          micStreamRef.current = null;
        }

        // Provide user feedback
        if (err.name === 'InvalidStateError' || err.message?.includes('already started')) {
          console.warn("[startBrowserRecording] Recognition already started, attempting to recover...");
          // Try to stop and restart after a delay
          setTimeout(() => {
            if (recognitionRef.current) {
              try {
                recognitionRef.current.stop();
              } catch (e) {
                console.warn("[startBrowserRecording] Error stopping recognition during recovery:", e);
              }
            }
          }, 100);
        } else {
          alert(`Failed to start speech recognition: ${err.message || err.name}. Please try again.`);
        }
        return; // Don't throw, just return to prevent further execution
      }
      // Set up audio analysis for waveform visualization (same as backendAudioTranscript)
      //startSilenceDetection(stream);
      drawWaveform(); // start drawing

    } catch (err) {
      console.error("[startBrowserRecording] Microphone access failed:", err);
      console.error("[startBrowserRecording] Error name:", err.name);
      console.error("[startBrowserRecording] Error message:", err.message);
      console.error("[startBrowserRecording] Full error:", err);

      setIsRecording(false);
      isRecordingRef.current = false;

      // Clean up any partial state
      if (micStreamRef.current) {
        try {
          micStreamRef.current.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
          });
        } catch (cleanupErr) {
          console.warn("[startBrowserRecording] Error cleaning up stream in catch block:", cleanupErr);
        }
        micStreamRef.current = null;
      }

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.abort();
        } catch (recErr) {
          console.warn("[startBrowserRecording] Error stopping recognition in catch block:", recErr);
        }
        recognitionRef.current = null;
      }

      isRecognitionStartingRef.current = false;

      // Provide specific error messages based on error type
      let errorMessage = "Microphone access failed. ";

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += "Please allow microphone access in your browser settings and try again.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += "No microphone found. Please connect a microphone and try again.";
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += "Microphone is already in use by another application. Please close other applications using the microphone and try again.";
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage += "Microphone constraints could not be satisfied. Please try again.";
      } else if (err.name === 'SecurityError') {
        errorMessage += "Microphone access blocked due to security restrictions. Please check your browser settings.";
      } else {
        errorMessage += `Error: ${err.message || 'Unknown error'}. Please check your browser permissions.`;
      }

      alert(errorMessage);
    }
  };

  const monitorBrowserIdealRecordingtimer = () => {
    // 1. Cancel any existing animation loop
    if (animationID !== null) {
      cancelAnimationFrame(animationID);
      animationID = null;
    }
    // CRITICAL: Reset the silent start time to NOW when timer is called
    // This ensures we wait the full duration from the last final result
    let silentStart = Date.now();
    let dataSubmitted = false;
    const checkSilence = () => {
      // Calculate elapsed time since last final result (in milliseconds)
      const elapsedMs = Date.now() - silentStart;
      const elapsedSeconds = Math.trunc(elapsedMs / 1000);
      const timeLeft = Math.max(0, sendMessageAfterSecondRef.current - elapsedSeconds);

      // Update UI with time left
      if (timeLeft >= 0) setTimeLeft(timeLeft);

      // Check if the silence period has passed
      // CRITICAL: Use >= to ensure we wait at least the full duration
      const silencePeriodMs = sendMessageAfterSecondRef.current * 1000;
      if (elapsedMs >= silencePeriodMs && dataSubmitted === false) {
        // CRITICAL: Only submit if bufferRef has content (final results), not messageRef (which may have interim)
        // Use bufferRef.current instead of messageRef.current to ensure we only submit finalized text
        const textToSubmit = bufferRef.current.trim();
        if (textToSubmit.length > 0) {
          dataSubmitted = true;
          console.log("[Auto-submit] Triggering auto-submission after silence period. Elapsed:", elapsedMs, "ms, Required:", silencePeriodMs, "ms. Text:", textToSubmit);
          // Set messageRef to buffer content (final results only) before sending
          messageRef.current = textToSubmit;
          setMessage(textToSubmit);
          sendMessage();
          bufferRef.current = "";
          if (recognitionRef.current) {
            recognitionRef.current.stop(); // Stop the continuous loop
          }
          setIsProcessing(true);
          isProcessingRef.current = true;
          // Set timeout to reset processing state if no reply received within 30 seconds
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
          }
          processingTimeoutRef.current = setTimeout(() => {
            if (isProcessingRef.current && isManualRecordingStop.current === false) {
              console.warn("Processing timeout: No reply received, resetting state");
              setIsProcessing(false);
              isProcessingRef.current = false;
              setIsRecording(true);
              startRecording();
            }
            processingTimeoutRef.current = null;
          }, 30000); // 30 second timeout
          // Stop the animation loop after submission
          if (animationID !== null) {
            cancelAnimationFrame(animationID);
            animationID = null;
          }
          return;
        }
      }
      animationID = requestAnimationFrame(checkSilence);
    };

    checkSilence();
  }
  // ⏹ Stop recording
  const stopRecordingUsingClick = () => {
    // Clear any pending processing timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    setIsProcessing(false);
    isRecordingRef.current = false;
    isProcessingRef.current = false;
    isManualRecordingStop.current = true;
    stopRecording();
  };
  // ⏹ Stop recording
  function stopRecording() {
    // Clear ALL pending recognition restart timeouts first
    recognitionRestartTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    recognitionRestartTimeoutsRef.current = [];
    // Reset restart counters
    recognitionRestartCountRef.current = 0;
    lastRecognitionEndTimeRef.current = 0;
    recognitionStartTimeRef.current = 0;

    // Stop the Web Speech API recognition - remove handlers then abort/stop
    if (recognitionRef.current) {
      try {
        // Remove handlers to avoid any restart logic from firing
        recognitionRef.current.onend = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onstart = null;
        // First try to stop it normally
        recognitionRef.current.stop();
        // Also try to abort if available (some browsers support this)
        if (typeof recognitionRef.current.abort === 'function') {
          recognitionRef.current.abort();
        }
      } catch (err) {
        console.warn("Error stopping recognition:", err);
      }
      // Clear the reference immediately to prevent any restarts
      recognitionRef.current = null;
    }
    // Reset the starting flag
    isRecognitionStartingRef.current = false;

    // Disconnect audio nodes to fully detach stream from AudioContext
    if (audioNodesRef.current.source) {
      try { audioNodesRef.current.source.disconnect(); } catch (_) { }
      audioNodesRef.current.source = null;
    }
    if (audioNodesRef.current.analyser) {
      try { audioNodesRef.current.analyser.disconnect(); } catch (_) { }
      audioNodesRef.current.analyser = null;
    }

    // Stop the stream tracks obtained via getUserMedia - this is critical for releasing the mic icon
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (_) { }
        t.enabled = false; // Explicitly disable the track
      });
      micStreamRef.current = null;
    }

    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream.getTracks().forEach((t) => {
            try { t.stop(); } catch (_) { }
            t.enabled = false;
          });
        }
      } catch (err) {
        console.warn("Error stopping media recorder:", err);
      }
      cancelAnimationFrame(animationFrameRef.current); // stop drawing/monitoring loop
    }

    // Also cancel the silence detection RAF loop if any
    if (animationID !== null) {
      cancelAnimationFrame(animationID);
      animationID = null;
    }

    // Fully close the AudioContext to release hardware in all browsers
    if (audioContextRef.current) {
      try {
        const ctx = audioContextRef.current;
        audioContextRef.current = null; // clear ref first to prevent reuse while closing
        ctx.close().catch(e => console.warn('AudioContext close failed:', e));
      } catch (e) {
        console.warn('AudioContext close threw:', e);
      }
    }

    // Reset waveform state and clear canvas
    rmsRef.current = 0;
    const canvas = waveCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    setIsRecording(false);
    isRecordingRef.current = false;
  }
  const silenceTimerRef = useRef(null);

  const startSilenceDetection = (stream) => {
    // Check if a context already exists in the ref. If not, create a new one.
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const audioCtx = audioContextRef.current; // Use the context from the ref


    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;

    // --- GAIN NODE IMPLEMENTATION ---
    const gainNode = audioCtx.createGain();
    // Set a gain value. 
    // 1.0 is no change. Use a value > 1.0 to increase volume/sensitivity.
    // Be CAREFUL: high values (e.g., > 5) can cause clipping/distortion.
    // Start with a small boost like 1.5 or 2.0.
    gainNode.gain.value = micSensitivity; // <--- INCREASED SENSITIVITY
    gainNodeRef.current = gainNode;

    // --- CONNECT THE NODES ---
    // Source (Mic) -> GainNode
    source.connect(gainNode);

    // GainNode -> Analyser (for VAD monitoring)

    // analyserNodeRef.current = analyser; // If you use this ref
    gainNode.connect(analyser); // <--- Analyser listens to amplified audio

    const dataArray = new Uint8Array(analyser.fftSize);
    //source.connect(analyser);

    let silentStart = null;

    const checkSilence = () => {
      analyser.getByteTimeDomainData(dataArray);

      // Compute RMS (volume level)
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        let v = (dataArray[i] - 128) / 128.0;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length) * 100; // 0–100 scale
      rmsRef.current = rms;

      //Capture loudest noise
      if (rms > maxRmsRef.current) {
        maxRmsRef.current = rms;
      }
      if (rms < silenceDetection) {
        if (!silentStart) silentStart = Date.now();
        if (isRecordingRef.current == true) {
          let timeLeft = Math.trunc((sendMessageAfterSecond * 1000 - (Date.now() - silentStart)) / 1000);
          if (timeLeft >= 0) setTimeLeft(timeLeft)
        }
        if (Date.now() - silentStart > sendMessageAfterSecond * 1000) {
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = true;
            //alert("Mic has been silent for more than 3 seconds!");
            console.log("silence detected")
            if (isManualRecordingStop.current == false) {
              setIsProcessing(true);
              isProcessingRef.current = true;
              console.log("2")

            }
            stopRecording();
          }
        }
      } else {
        // Reset if user speaks
        silentStart = null;
        silenceTimerRef.current = null;
      }

      requestAnimationFrame(checkSilence);
    };

    checkSilence();
    // RETURN THE MODIFIED STREAM (GainNode's output)
    return gainNode;
  };

  function get_auth_token() {
    const authKey = localStorage.getItem("auth");
    if (authKey) {
      const localData = JSON.parse(authKey);
      return `Bearer ${localData.token}`;
    }
  }

  function get_request_header(include_content_type = true) {
    let header = { "Authorization": get_auth_token() }
    if (include_content_type == true) {
      header["Content-Type"] = "application/json"
    }
    return header;
  }

  /**
   * Fetch both TTS audio and visemes in parallel, then play them together for sync
   */
  async function fetchTTSAndVisemesParallel(text, isCrying = false) {
    // [NEW] Check mute state using REF to avoid stale closures
    if (isMutedRef.current) {
      console.log("[TTS] System is MUTED (checked via ref). Skipping API calls.");
      return { audioResponse: null, visemesData: null };
    }

    let voice_id = state.patient_profile.voice_id;
    let virtual_profile = state.patient_profile.virtual_profile; // Get virtual_profile with speech style

    if (!text) {
      console.error(`[TTS Frontend] ERROR: text is null or undefined!`);
      return { audioResponse: null, visemesData: null };
    }

    // Start both API calls in parallel - backend will handle asterisk conversion via ssml_generator.py
    const [audioResponse, visemesResponse] = await Promise.all([

      // TTS Audio API call - include virtual_profile for speech characteristics and is_crying flag
      fetch(process.env.REACT_APP_BACKEND_URL + "/api/tts/audio/", {
        method: "POST",
        headers: get_request_header(),
        body: JSON.stringify({ text, voice_id, virtual_profile, is_crying: isCrying }),
      }),
      // Visemes API call - include virtual_profile for speech characteristics and is_crying flag
      fetch(process.env.REACT_APP_BACKEND_URL + "/api/tts/visemes/", {
        method: "POST",
        headers: get_request_header(),
        body: JSON.stringify({ text, voice_id, virtual_profile, is_crying: isCrying }),
      })
    ]);

    // Check both responses
    if (!audioResponse.ok || !audioResponse.body) {
      console.error("TTS audio request failed");
      return { audioResponse: null, visemesData: null };
    }

    if (!visemesResponse.ok) {
      console.error("Visemes request failed");
      return { audioResponse, visemesData: null };
    }

    // Parse visemes response
    const visemesData = await visemesResponse.json();
    setReplyText(visemesData.reply);
    // Set speechMarks to the visemes array, not the entire object
    const visemesArray = visemesData.visemes || visemesData;
    if (Array.isArray(visemesArray)) {
      console.log(`[Lip Sync] Setting ${visemesArray.length} visemes for lip syncing`);
      setSpeechMarks(visemesArray);
    } else {
      console.warn("[Lip Sync] Visemes data is not an array:", visemesArray);
      setSpeechMarks(null);
    }

    return { audioResponse, visemesData };
  }

  async function endSession() {
    // Always show popup; buttons handle close/skip/save paths
    setShowReconstructionPopup(true);
  }

  const handleReconstructionSave = async (hasContent) => {
    setShowReconstructionPopup(false);
    setEndingSession(true); // Disable button during API call

    // Proceed with ending session
    try {
      const payload = {
        patient_id: state.patient_profile.id,
        consultation_id: consultation_id,
        doctor_id: doctor_id
      };

      const res = await api.endaichatsession(payload);
      if (res.status != 200) throw new Error(res.data?.error || "Failed to end session");

      // Check for achievements
      if (res.data.new_achievements && res.data.new_achievements.length > 0) {
        const achievement = res.data.new_achievements[0];
        setToastNotification({
          type: 'achievement',
          title: 'Achievement Unlocked!',
          message: `You unlocked: ${achievement.name}!`
        });
        // Wait for toast to be seen
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      // Allow navigation after ending session
      shouldBlockNavigationRef.current = false;
      // alert("Session ended successfully!"); // Removed alert in favor of smooth exit or toast
      navigate("/appointment");
      // Note: setEndingSession(false) not needed here since we navigate away
    } catch (err) {
      console.error("Unable to end the session:", err);
      alert(err.message || "Failed to end session");
      setEndingSession(false); // Re-enable button on error
    }
  };

  const handleReconstructionSkip = async () => {
    setShowReconstructionPopup(false);
    setEndingSession(true); // Disable button during API call

    // Proceed with ending session
    try {
      const payload = {
        patient_id: state.patient_profile.id,
        consultation_id: consultation_id,
        doctor_id: doctor_id
      };

      const res = await api.endaichatsession(payload);
      if (res.status != 200) throw new Error(res.data?.error || "Failed to end session");

      // Check for achievements
      if (res.data.new_achievements && res.data.new_achievements.length > 0) {
        const achievement = res.data.new_achievements[0];
        setToastNotification({
          type: 'achievement',
          title: 'Achievement Unlocked!',
          message: `You unlocked: ${achievement.name}!`
        });
        // Wait for toast to be seen
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      // Allow navigation after ending session
      shouldBlockNavigationRef.current = false;
      // alert("Session ended successfully!");
      navigate("/appointment");
      // Note: setEndingSession(false) not needed here since we navigate away
    } catch (err) {
      console.error("Unable to end the session:", err);
      alert(err.message || "Failed to end session");
      setEndingSession(false); // Re-enable button on error
    }
  };

  async function restartSession() {
    // Show confirmation dialog before proceeding
    const confirmed = window.confirm(
      "⚠️ WARNING: This will clear ALL details and discussions for this session.\n\n" +
      "All conversation history and session data will be permanently deleted.\n\n" +
      "This action cannot be undone. Are you sure you want to continue?"
    );

    if (!confirmed) {
      console.log("[Restart Session] User cancelled restart");
      return;
    }

    try {
      const payload = {
        patient_id: state.patient_profile.id,
        consultation_id: consultation_id,
        doctor_id: doctor_id
      };

      const res = await api.restartChatSession(payload);
      if (res.status !== 200) {
        throw new Error(res.data?.error || "Failed to restart session");
      }

      // Clear all messages
      setMessages([]);

      // Clear message input
      setMessage("");
      messageRef.current = "";
      bufferRef.current = "";

      // Clear reply text and speech marks
      setReplyText(null);
      setSpeechMarks(null);

      // Stop any ongoing TTS playback
      if (currentTTSAudioRef.current) {
        try {
          currentTTSAudioRef.current.pause();
          currentTTSAudioRef.current = null;
        } catch (e) {
          console.warn("Error stopping TTS audio:", e);
        }
      }

      // Stop any ongoing recording
      if (isRecordingRef.current) {
        try {
          stopRecording();
        } catch (e) {
          console.warn("Error stopping recording:", e);
        }
      }

      // Rejoin WebSocket room to reinitialize the session on the backend
      // Wait a brief moment to ensure backend has finished clearing
      setTimeout(() => {
        const currentSocket = socketRef.current || socket;
        if (currentSocket && currentSocket.connected) {
          console.log("[Restart Session] Rejoining WebSocket room...");
          currentSocket.emit("join", { state: state });
        } else {
          console.warn("[Restart Session] Socket not connected, cannot rejoin room");
          // If socket is not connected, try to reconnect
          if (socketRef.current) {
            console.log("[Restart Session] Attempting to reconnect socket...");
            socketRef.current.connect();
            socketRef.current.once("connect", () => {
              console.log("[Restart Session] Socket reconnected, joining room...");
              socketRef.current.emit("join", { state: state });
            });
          }
        }
      }, 100); // Small delay to ensure backend has finished processing

      alert("Session restarted successfully! Chat window has been cleared.");
    } catch (err) {
      console.error("Unable to restart the session:", err);
      alert(err.message || "Failed to restart session");
    }
  }
  async function onTimerUpdate(countdown) {
    if (countdown == 120 || (countdown > 121 && countdown % 1800 == 60)) {
      try {
        await api.notifychattimer();
        setCreditUsed(creditUsed + 1);
      } catch (err) {
        // Handle network errors
        if (err.code === "ERR_NETWORK" || err.message?.includes("Network Error")) {
          console.error("[Timer] Network error:", err);
          // Don't alert on network errors - they might be temporary
          // Just log and continue
          return;
        }

        // Handle 402 Payment Required (insufficient credits)
        const status = err.response?.status || err.status;
        if (status === 402) {
          console.error("[Timer] Insufficient credits:", err);
          if (!lowBalance) {
            alert("Your balance is low. Please recharge.");
            setLowBalance(true);
            // Stop the session if balance is too low
            shouldBlockNavigationRef.current = false;
            navigate("/appointment");
          }
          return;
        }

        // Handle other errors
        console.error("[Timer] Unexpected error:", err);
      }
    }
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Wrapper function for safe navigation
  const safeNavigate = useRef((to, options) => {
    if (shouldBlockNavigationRef.current && location.pathname === '/chat') {
      setShowNavigationConfirm(true);
      pendingNavigationRef.current = { to, options, type: 'router' };
      return;
    }
    navigate(to, options);
  });

  // Listen for navigation allowance from Sidebar
  useEffect(() => {
    const handleAllowNavigation = () => {
      shouldBlockNavigationRef.current = false;
    };

    window.addEventListener('allowNavigation', handleAllowNavigation);

    return () => {
      window.removeEventListener('allowNavigation', handleAllowNavigation);
    };
  }, []);

  // Handle browser navigation (back/forward/close/refresh)
  useEffect(() => {
    if (location.pathname !== '/chat') {
      shouldBlockNavigationRef.current = false;
      return;
    }

    // Set blocking when on chat page
    shouldBlockNavigationRef.current = true;

    const handleBeforeUnload = (e) => {
      if (shouldBlockNavigationRef.current) {
        e.preventDefault();
        // Note: Modern browsers ignore custom messages and show their own generic message
        // But we set it anyway for older browsers
        e.returnValue = "You have been charged credits to initiate this conversation. Leaving or refreshing the page will result in loss of your charged credits. Do you want to proceed?";
        return e.returnValue;
      }
    };

    // Handle browser back/forward buttons using history API
    const handlePopState = (e) => {
      if (shouldBlockNavigationRef.current) {
        // Prevent the navigation
        window.history.pushState(null, '', '/chat');
        setShowNavigationConfirm(true);
        pendingNavigationRef.current = { type: 'browser' };
      }
    };

    // Intercept keyboard shortcuts for refresh (F5, Ctrl+R, Ctrl+Shift+R)
    const handleKeyDown = (e) => {
      if (!shouldBlockNavigationRef.current) return;

      // F5 or Ctrl+R or Ctrl+Shift+R (refresh)
      if (e.key === 'F5' ||
        (e.ctrlKey && e.key === 'r') ||
        (e.ctrlKey && e.shiftKey && e.key === 'R')) {
        e.preventDefault();
        e.stopPropagation();
        setShowNavigationConfirm(true);
        pendingNavigationRef.current = { type: 'refresh' };
        return false;
      }
    };

    // Add a history entry to intercept back button
    window.history.pushState(null, '', '/chat');

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [location.pathname]);

  // Handle navigation confirmation
  const handleConfirmNavigation = () => {
    shouldBlockNavigationRef.current = false;
    setShowNavigationConfirm(false);
    if (pendingNavigationRef.current) {
      if (pendingNavigationRef.current.type === 'router') {
        // React Router navigation
        if (pendingNavigationRef.current.targetPath) {
          navigate(pendingNavigationRef.current.targetPath);
        } else if (pendingNavigationRef.current.to) {
          navigate(pendingNavigationRef.current.to, pendingNavigationRef.current.options);
        }
      } else if (pendingNavigationRef.current.type === 'browser') {
        // Browser back/forward navigation - allow it
        window.history.back();
      } else if (pendingNavigationRef.current.type === 'refresh') {
        // For refresh, temporarily disable blocking and then refresh
        shouldBlockNavigationRef.current = false;
        // Use setTimeout to ensure state is updated before refresh
        setTimeout(() => {
          window.location.reload();
        }, 0);
      }
      pendingNavigationRef.current = null;
    }
  };

  // Handle navigation cancellation
  const handleCancelNavigation = () => {
    setShowNavigationConfirm(false);
    if (pendingNavigationRef.current && pendingNavigationRef.current.type === 'browser') {
      // Push state back to prevent navigation
      window.history.pushState(null, '', '/chat');
    }
    pendingNavigationRef.current = null;
  };

  // Meter component for displaying patient metrics (speedometer gauge style)
  const PatientMeter = ({ label, value, maxValue = 100, color = "blue", isString = false }) => {
    // Map breadcrumb levels to numeric values for display
    const breadcrumbMap = { "none": 0, "avoid": 20, "hint": 40, "partial": 70, "full": 100 };
    // Map moods to numeric values for display
    const moodMap = { "neutral": 50, "sad": 30, "anxious": 60, "angry": 80, "scared": 70, "overwhelmed": 90 };

    let displayValue = value;
    let percentage = 0;

    if (isString) {
      if (label === "TBP") {
        displayValue = value;
        percentage = breadcrumbMap[value] || 0;
      } else if (label === "Mood") {
        displayValue = value;
        percentage = moodMap[value] || 50;
      }
    } else {
      displayValue = typeof value === 'number' ? value.toFixed(1) : value;
      percentage = Math.min((value / maxValue) * 100, 100);
    }

    const size = 120;
    const strokeWidth = 12;
    const radius = 50;
    const centerX = size / 2;
    const centerY = size / 2 + 20; // Moved down to be closer to labels
    const needleLength = radius - 5;

    // Calculate arc for upward semicircle (180° to 0°)
    const startAngle = Math.PI; // 180 degrees (left)
    const endAngle = 0; // 0 degrees (right)

    // Calculate needle angle based on percentage (0% = left, 100% = right)
    const needleAngle = startAngle - (percentage / 100) * Math.PI;

    // Calculate needle endpoint
    const needleX = centerX + needleLength * Math.cos(needleAngle);
    const needleY = centerY - needleLength * Math.sin(needleAngle);

    // Determine color order: Intensity and Mood use green-to-red, others use red-to-green
    const isGreenToRed = label === "Intensity" || label === "Mood";

    // Create colored segments (zones) for the gauge
    // For Intensity and Mood: Green (low) to Red (high)
    // For others: Red (low) to Green (high)
    const segments = isGreenToRed ? [
      { start: 0, end: 33, color: "#10b981" }, // Green
      { start: 33, end: 50, color: "#84cc16" }, // Light green
      { start: 50, end: 66, color: "#eab308" }, // Yellow
      { start: 66, end: 83, color: "#f97316" }, // Orange
      { start: 83, end: 100, color: "#ef4444" }, // Red
    ] : [
      { start: 0, end: 33, color: "#ef4444" }, // Red
      { start: 33, end: 50, color: "#f97316" }, // Orange
      { start: 50, end: 66, color: "#eab308" }, // Yellow
      { start: 66, end: 83, color: "#84cc16" }, // Light green
      { start: 83, end: 100, color: "#10b981" }, // Green
    ];

    // Create arc paths for each segment
    const createSegmentPath = (startPercent, endPercent) => {
      const startAngle = Math.PI - (startPercent / 100) * Math.PI;
      const endAngle = Math.PI - (endPercent / 100) * Math.PI;
      const x1 = centerX + radius * Math.cos(startAngle);
      const y1 = centerY - radius * Math.sin(startAngle);
      const x2 = centerX + radius * Math.cos(endAngle);
      const y2 = centerY - radius * Math.sin(endAngle);
      const largeArc = (endPercent - startPercent) > 50 ? 1 : 0;
      return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
    };

    return (
      <div className="flex flex-col items-center mx-3">
        <div className="relative overflow-visible" style={{ width: size, height: size / 2 + 45 }}>
          <svg
            width={size}
            height={size / 2 + 45}
            viewBox={`0 0 ${size} ${size / 2 + 45}`}
            className="overflow-visible"
          >
            <defs>
              {/* Gradient definitions for segments */}
              {/* For Intensity and Mood: Green (low) to Red (high), For others: Red (low) to Green (high) */}
              <linearGradient id={`gradient-${label}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={isGreenToRed ? "#10b981" : "#ef4444"} stopOpacity="0.8" />
                <stop offset="50%" stopColor="#eab308" stopOpacity="0.8" />
                <stop offset="100%" stopColor={isGreenToRed ? "#ef4444" : "#10b981"} stopOpacity="0.8" />
              </linearGradient>
            </defs>

            {/* Colored segments (zones) with better visibility */}
            {segments.map((segment, idx) => (
              <path
                key={idx}
                d={createSegmentPath(segment.start, segment.end)}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                opacity={0.85}
              />
            ))}

            {/* Needle - rotated based on percentage */}
            <g>
              <line
                x1={centerX}
                y1={centerY}
                x2={needleX}
                y2={needleY}
                stroke="#ffffff"
                strokeWidth={4}
                strokeLinecap="round"
                className="transition-all duration-500 ease-out"
                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }}
              />
            </g>

            {/* Pivot point (center circle) with shadow */}
            <circle
              cx={centerX}
              cy={centerY}
              r={7}
              fill="#ffffff"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }}
            />
            <circle
              cx={centerX}
              cy={centerY}
              r={5}
              fill="#1a1a1a"
            />
          </svg>

          {/* Value display above the arch */}
          <div className="absolute left-1/2 transform -translate-x-1/2 text-center whitespace-nowrap" style={{ top: '-5px', minWidth: '60px' }}>
            <span className="text-lg font-bold text-white drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              {typeof displayValue === 'number' ? displayValue.toFixed(0) : displayValue}
            </span>
            {!isString && <span className="text-xs text-gray-200 font-semibold ml-0.5">%</span>}
          </div>
        </div>

        {/* Label at bottom with better styling */}
        <span className="text-sm font-bold text-white mt-3 text-center uppercase tracking-wide" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
          {label}
        </span>
      </div>
    );
  };

  // Compact helper renderers for insight panel (defensive against missing fields)
  const renderDoctorStyleSummary = (state) => {
    if (!state) return "Style not detected yet.";
    const style = state.current_style || state.dominant_style || "Unknown";
    const trend = state.confidence_trend || state.style_confidence_trend || "";
    return `Perceived style: ${style}${trend ? ` (confidence: ${trend})` : ""}`;
  };

  const renderAllianceSummary = (alliance) => {
    if (!alliance) return "Alliance metrics will appear here as you talk.";
    const rapport = typeof alliance.rapport_score === "number" ? alliance.rapport_score.toFixed(1) : "–";
    const strength = typeof alliance.alliance_strength === "number" ? alliance.alliance_strength.toFixed(1) : "–";
    const lastEvent = alliance.rupture_events && alliance.rupture_events.length
      ? alliance.rupture_events[alliance.rupture_events.length - 1].type || "recent rupture"
      : "no recent ruptures";
    return `Rapport: ${rapport} | Alliance: ${strength} | ${lastEvent}`;
  };

  const renderTriggersSummary = (ts) => {
    if (!ts || !ts.known_triggers) return "Recent triggers will show here.";
    const triggers = Object.keys(ts.known_triggers || {}).slice(0, 3);
    if (!triggers.length) return "No strong triggers detected yet.";
    return `Top triggers: ${triggers.join(", ")}`;
  };

  const renderCopingSummary = (cm) => {
    if (!cm) return "Coping patterns will appear here.";
    const active =
      cm.adaptive_strategies ||
      cm.active_coping_strategies ||
      [];
    if (!active.length) return "No dominant coping strategy detected yet.";
    return `Active coping: ${active.slice(0, 3).join(", ")}`;
  };

  const renderPatternsSummary = (bp) => {
    if (!bp) return "Behavioral patterns will appear here over time.";
    const patterns = (bp.most_frequent || [])
      .map((p) => (p.pattern_id ? p.pattern_id : p))
      .slice(0, 3);
    if (!patterns.length) return "No clear recurring pattern detected yet.";
    return `Patterns: ${patterns.join(", ")}`;
  };

  const renderProgressSummary = (pt) => {
    if (!pt) return "Session-level progress will accumulate here.";
    const totalSessions = pt.total_sessions ?? 0;
    const latest = pt.latest_session || {};
    const sessionNo = latest.session_number ?? "–";
    const trustChange =
      typeof latest.trust_change === "number"
        ? latest.trust_change.toFixed(1)
        : "–";
    const maxLevel = latest.max_disclosure || "–";
    return `Session ${sessionNo}/${totalSessions} | Δ trust: ${trustChange} | Max disclosure: ${maxLevel}`;
  };

  const renderSessionMetricsSummary = (sm) => {
    if (!sm) return null;
    const overall = sm.overall_score ?? sm.composite_score;
    if (overall === undefined) return null;
    return `Overall session score (internal): ${overall.toFixed ? overall.toFixed(1) : overall}`;
  };

  const drawWaveform = () => {
    const canvas = waveCanvasRef.current;
    if (!canvas) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
      return;
    }
    const ctx = canvas.getContext("2d");

    // If recording has been stopped, stop drawing and clear
    if (!isRecordingRef.current) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const width = canvas.width;
    const height = canvas.height;

    // Shift existing pixels left by 1px
    const imageData = ctx.getImageData(1, 0, width - 1, height);
    ctx.putImageData(imageData, 0, 0);

    // Clear the rightmost column
    ctx.clearRect(width - 1, 0, 1, height);

    // Current RMS (0-100)
    const rmsValue = Math.min(rmsRef.current, 100);
    const barHeight = (rmsValue / 100) * height;

    // Draw one pixel column at the right edge
    ctx.fillStyle = "#00ff00";
    ctx.fillRect(width - 1, (height - barHeight) / 2, 1, barHeight);

    animationFrameRef.current = requestAnimationFrame(drawWaveform);
  };

  // Store the selected background image in state - selected once per component mount
  // This ensures a new random image is selected each time user lands on /chat page
  // but the same image persists during fullscreen toggles within the same session
  const [selectedBackground] = useState(() => {
    // Randomly select a background image when component first mounts
    const backgroundImages = [
      "/textures/bg-1.jpg",
      "/textures/bg-2.jpg",
      "/textures/bg-3.jpg",
      "/textures/bg-4.jpg",
      "/textures/bg-5.jpg",
      "/textures/bg-6.jpg",
      "/textures/bg-7.jpg",
      "/textures/bg-9.jpg",
      "/textures/bg-10.jpg",
      "/textures/bg-11.jpg",
      "/textures/bg-12.jpg",
    ];
    const randomIndex = Math.floor(Math.random() * backgroundImages.length);
    return backgroundImages[randomIndex];
  });

  const avatarCanvasElement = useMemo(() => {
    return (
      <Suspense fallback={<div>Connecting with patient...</div>}>
        <Canvas shadows camera={{ position: [0, 0, 8], fov: 42 }} className="h-full w-full">
          <color attach="background" args={["#ececec"]} />
          <Experience
            avatar_Id={patient_profile.avatar_val}
            text={replyText}
            speechMarks={speechMarks}
            isCrying={isCrying}
            backgroundImage={selectedBackground}
          />
        </Canvas>
      </Suspense>
    );
  }, [patient_profile.avatar_val, replyText, speechMarks, isCrying, selectedBackground]);

  return (
    <DashboardLayout hideSidebar={isAvatarFullscreen}>
      {/* Navigation Confirmation Dialog */}
      {showNavigationConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Leave Chat Session?</h3>
            <p className="text-gray-600 mb-6">
              You have been charged credits to initiate this conversation with the patient.
              Leaving or refreshing this page will result in the loss of your charged credits.
              Are you sure you want to proceed?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelNavigation}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmNavigation}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Leave Session
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col h-[100vh] overflow-hidden -mt-16 -mx-4 -mb-4 md:-mt-8 md:-mx-8 md:-mb-8" data-tour="session-interface">
        {!isAvatarFullscreen && (
          <>
            {/* Mobile Layout */}
            {/* Mobile Layout - Polished */}
            <div className="md:hidden flex flex-col h-full relative bg-gray-50">
              {/* Header Card */}
              <div className="px-4 pt-4 pb-2 z-10">
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg">
                        {patient_profile.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{patient_profile.name}</h3>
                        <p className="text-xs text-green-500 font-medium flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                          Live Session
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="p-2 bg-gray-50 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-xs font-medium text-gray-500 border-t border-gray-50 pt-3 mt-1">
                    <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                      <Clock className="w-3.5 h-3.5" />
                      <TimeTracker onTimerUpdate={onTimerUpdate} />
                    </div>
                    <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>{creditUsed} Credits</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visualizer / Avatar Area */}
              <div className="flex-1 relative w-full overflow-hidden flex items-center justify-center">
                {avatarCanvasElement}

                {/* Floating Controls Overlay */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 z-20">
                  <button
                    onClick={restartSession}
                    className="w-10 h-10 bg-white/90 backdrop-blur rounded-full shadow-lg flex items-center justify-center text-orange-500 border border-white/50"
                    title="Restart"
                  >
                    <span className="text-xl">↻</span>
                  </button>

                  {/* Mic Control - Large Center Button */}
                  {!isProcessing && (
                    <button
                      onClick={!isRecording ? startRecording : stopRecordingUsingClick}
                      className={`w-16 h-16 rounded-full flex items-center justify-center shadow-xl border-4 border-white transition-all transform hover:scale-105 active:scale-95 ${isRecording ? 'bg-red-500 shadow-red-500/40' : 'bg-blue-600 shadow-blue-600/40'
                        }`}
                    >
                      {isRecording ? <MicOff className="w-7 h-7 text-white" /> : <Mic className="w-7 h-7 text-white" />}
                    </button>
                  )}
                  {isProcessing && (
                    <div className="w-16 h-16 rounded-full flex items-center justify-center bg-yellow-400 shadow-xl border-4 border-white animate-pulse">
                      <span className="text-2xl">⏳</span>
                    </div>
                  )}

                  <button
                    onClick={endSession}
                    disabled={endingSession}
                    className="w-10 h-10 bg-white/90 backdrop-blur rounded-full shadow-lg flex items-center justify-center text-red-500 border border-white/50"
                    title="End Session"
                    data-tour="generate-summary"
                  >
                    <span className="text-xl">✕</span>
                  </button>
                </div>

                {/* Fullscreen Toggle (Top Right of Avatar) */}
                <button
                  onClick={() => updateAvatarFullscreen(!isAvatarFullscreen)}
                  className="absolute top-2 right-4 w-8 h-8 bg-black/20 text-white rounded-full backdrop-blur-sm flex items-center justify-center"
                >
                  {isAvatarFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              </div>

              {/* Chat Input Area */}
              <div className="p-4 bg-white border-t border-gray-100 z-10 pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                <div className="flex gap-3 items-end">
                  <div className="flex-1 relative">
                    <textarea
                      rows={1}
                      placeholder="Type a message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      className="w-full bg-gray-100 border-0 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none max-h-24"
                      style={{ minHeight: '44px' }}
                    />
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!message.trim()}
                    className="bg-blue-600 text-white p-3 rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors flex-shrink-0"
                  >
                    <Send className="w-5 h-5 ml-0.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Layout - Elegance Redesign */}
            <div className="hidden md:flex flex-col h-full bg-secondary-50 relative overflow-hidden">

              {/* Top Header - Glass Effect */}
              <div className="flex-none px-6 py-2 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 supports-[backdrop-filter]:bg-white/60">
                <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-base shadow-sm">
                        {patient_profile.name.charAt(0)}
                      </div>
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></span>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-secondary-800 tracking-tight flex items-center gap-2">
                        {patient_profile.name}
                        <span className="px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-500 text-[9px] font-medium uppercase tracking-wider">Patient</span>
                      </h3>
                      <div className="flex items-center gap-1 text-[11px] text-secondary-500 font-medium">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <TimeTracker onTimerUpdate={onTimerUpdate} />
                        </div>
                        <span className="w-0.5 h-0.5 rounded-full bg-secondary-300"></span>
                        <div className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          <span>{creditUsed} Credits</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateAvatarFullscreen(!isAvatarFullscreen)}
                      className="px-3 py-1.5 text-[11px] font-semibold text-secondary-600 bg-secondary-100 hover:bg-secondary-200 rounded-full transition-all flex items-center gap-1.5"
                    >
                      {isAvatarFullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                      {isAvatarFullscreen ? "Exit Focus" : "Focus"}
                    </button>
                    <div className="h-5 w-px bg-secondary-200 mx-1"></div>
                    <button
                      onClick={restartSession}
                      className="p-1.5 text-secondary-400 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-colors"
                      title="Restart Session"
                    >
                      <span className="text-base leading-none">↻</span>
                    </button>
                    <button
                      onClick={endSession}
                      disabled={endingSession}
                      className={`px-4 py-1.5 rounded-full text-[11px] font-bold transition-all shadow-sm ${endingSession
                        ? "bg-secondary-100 text-secondary-400 cursor-not-allowed"
                        : "bg-rose-500 text-white hover:bg-rose-600 hover:shadow-rose-200"
                        }`}
                    >
                      {endingSession ? "Ending..." : "End Session"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-grow flex overflow-hidden max-w-7xl mx-auto w-full p-6 gap-6">

                {/* Left: Avatar/Visualizer */}
                <div
                  className={`relative transition-all duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)] ${isAvatarFullscreen ? "w-0 opacity-0 hidden" : "w-[65%] lg:w-[70%]"
                    } bg-black/5 rounded-3xl overflow-hidden shadow-inner flex flex-col`}
                >
                  {/* Avatar Container inner wrapper */}
                  <div className="absolute inset-0">
                    {avatarCanvasElement}
                  </div>
                  {/* Overlay Gradient for cinematics using absolute positioning */}
                  <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_100px_rgba(0,0,0,0.1)] rounded-3xl"></div>
                </div>

                {/* Right: Chat Interface */}
                {!isAvatarFullscreen && (
                  <div className="w-[35%] lg:w-[30%] flex flex-col h-full bg-white rounded-3xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)] border border-slate-100 overflow-hidden">

                    {/* Chat Header */}
                    <div className="px-5 py-3 border-b border-secondary-50 bg-secondary-50/50 flex items-center justify-between">
                      <span className="text-xs font-bold text-secondary-400 uppercase tracking-widest">Live Transcript</span>
                      <div className={`w-2 h-2 rounded-full ${isProcessing ? "bg-yellow-400 animate-pulse" : "bg-emerald-400"}`}></div>
                    </div>

                    {/* Messages List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-6 scroll-smooth custom-scrollbar">
                      {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-secondary-300 opacity-80">
                          <div className="w-16 h-16 rounded-full bg-secondary-50 flex items-center justify-center mb-3">
                            <span className="text-2xl grayscale opacity-50">👋</span>
                          </div>
                          <p className="text-sm font-medium">Session Ready</p>
                          <p className="text-xs">Start speaking or typing...</p>
                        </div>
                      ) : (
                        messages.map((m, idx) => {
                          const isUser = m.role === 'user' || m.role === 'student'; // Handle both 'user' and 'student' roles for safety
                          const roleLabel = isUser ? 'You' : 'Patient';

                          // Format timestamp
                          let timeStr = "";
                          if (m.timestamp) {
                            try {
                              const date = new Date(m.timestamp);
                              timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                            } catch (e) { console.warn("Invalid timestamp", e); }
                          } else {
                            // If no timestamp (e.g. system message or legacy), defaulting to empty or "Now" if it's very recent? 
                            // For now, let's leave it blank if missing to avoid confusion, or could assume "Now" for live.
                            // timeStr = "Now"; 
                          }

                          // Skip system messages if needed, or render differently? 
                          // Currently logic renders all. 

                          return (
                            <div key={idx} className={`group flex flex-col ${isUser ? 'items-end' : 'items-start'} animate-fade-in-up`}>
                              <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[92%]`}>
                                <div
                                  className={`px-5 py-3.5 text-[13.5px] leading-relaxed relative ${isUser
                                    ? 'bg-gradient-to-br from-primary-600 to-primary-500 text-white rounded-2xl rounded-tr-sm shadow-md shadow-primary-200/50'
                                    : 'bg-secondary-50 text-secondary-700 rounded-2xl rounded-tl-sm border border-secondary-100'
                                    }`}
                                >
                                  {m.content}
                                </div>

                                {/* Timestamp & Sender Label */}
                                <div className="flex items-center gap-1 mt-1 px-1">
                                  <span className="text-[10px] font-bold text-secondary-400">
                                    {roleLabel}
                                  </span>
                                  {timeStr && (
                                    <>
                                      <span className="text-[8px] text-secondary-300">•</span>
                                      <span className="text-[10px] text-secondary-300">
                                        {timeStr}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>


                    {/* Microphone conflict notification */}
                    {/* Premium Input Capsule */}
                    <div className="p-4 bg-white border-t border-slate-50">
                      {/* Microphone Conflict Alert */}
                      {micConflictMessage && (
                        <div className="mb-2 p-2.5 text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-lg flex items-center gap-2 animate-pulse">
                          <span className="text-base">⚠️</span> {micConflictMessage}
                        </div>
                      )}

                      <div className="relative flex items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-[24px] p-2 shadow-sm focus-within:ring-2 focus-within:ring-primary-100 focus-within:border-primary-300 transition-all">

                        {/* Mic Toggle (Integrated) */}
                        <button
                          onClick={!isRecording ? startRecording : stopRecordingUsingClick}
                          disabled={isProcessing}
                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${isProcessing
                            ? "bg-amber-100 text-amber-600 cursor-wait"
                            : isRecording
                              ? "bg-rose-100 text-rose-600 animate-pulse"
                              : "bg-white text-secondary-400 hover:bg-primary-50 hover:text-primary-600 shadow-sm border border-secondary-100"
                            }`}
                          title={isRecording ? "Mute Microphone" : "Unmute Microphone"}
                        >
                          {isProcessing ? (
                            <span className="text-lg">⏳</span>
                          ) : isRecording ? (
                            <MicOff className="w-5 h-5" />
                          ) : (
                            <Mic className="w-5 h-5" />
                          )}
                        </button>

                        {/* Text Input */}
                        <textarea
                          rows={1}
                          placeholder={isRecording ? "Listening..." : "Type your message..."}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          className="flex-1 bg-transparent border-0 focus:ring-0 resize-none py-2.5 px-2 text-sm text-secondary-700 placeholder:text-secondary-400 max-h-32"
                          style={{ minHeight: '44px' }}
                        />

                        {/* Send Button */}
                        <button
                          onClick={sendMessage}
                          disabled={!message.trim() || isProcessing}
                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-md ${!message.trim() || isProcessing
                            ? "bg-secondary-200 text-secondary-400 cursor-not-allowed shadow-none"
                            : "bg-primary-600 text-white hover:bg-primary-700 hover:scale-105 hover:shadow-primary-200"
                            }`}
                        >
                          <Send className="w-4 h-4 ml-0.5" />
                        </button>
                      </div>

                      {/* Status Footer */}
                      <div className="flex justify-between items-center px-2 mt-2">
                        <span className="text-[10px] text-secondary-400 font-medium">
                          {isRecording ? "Microphone active" : "Press Enter to send"}
                        </span>
                        {isProcessing && (
                          <span className="text-[10px] font-bold text-amber-500 flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full">
                            Processing ({timeLeft}s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {isAvatarFullscreen && (
          <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
            {/* Microphone conflict notification - Centered at top for better visibility */}
            {micConflictMessage && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 p-3 text-sm md:text-base font-medium text-white bg-red-600 rounded-lg border-2 border-red-400 shadow-lg max-w-md mx-4" role="alert">
                <div className="flex items-center gap-2">
                  <span className="text-xl">⚠️</span>
                  <span>{micConflictMessage}</span>
                </div>
              </div>
            )}
            {/* Controls at top right - Responsive */}
            <div className="absolute top-2 md:top-4 right-2 md:right-4 z-10 flex flex-col items-end gap-2">
              {/* Recording button and waveform */}
              <div className="flex flex-col md:flex-row items-center gap-2 bg-black/60 rounded-lg p-2 backdrop-blur-sm">
                {isProcessing ? (
                  <button className="px-3 md:px-4 py-2 bg-yellow-500 text-white rounded text-sm md:text-base" disabled>
                    ⏳ Processing...
                  </button>
                ) : !isRecording ? (
                  <button onClick={async () => {
                    console.log("[Unmute] Button clicked in fullscreen mode");
                    isManualRecordingStop.current = false;
                    bufferRef.current = "";
                    setMessage("");
                    messageRef.current = "";
                    // Ensure we're not in a processing state
                    setIsProcessing(false);
                    isProcessingRef.current = false;
                    await startRecording();
                  }} className="px-3 md:px-4 py-2 bg-green-500 text-white rounded text-sm md:text-base">
                    🎤 Unmute Mic
                  </button>
                ) : (
                  <button onClick={stopRecordingUsingClick} id="btnStopRecording" className="px-3 md:px-4 py-2 bg-red-500 text-white rounded text-sm md:text-base">
                    ⏹ Mute Mic
                  </button>
                )}
                <canvas
                  ref={waveCanvasRef}
                  width={150}
                  height={50}
                  className="border border-gray-300 rounded"
                />
              </div>
              {/* Exit button */}
              <button
                onClick={() => updateAvatarFullscreen(false)}
                className="px-2 md:px-3 py-1 text-xs md:text-sm bg-white text-gray-800 rounded hover:bg-gray-100"
              >
                Exit Full Screen ✕
              </button>
            </div>
            {/* Avatar canvas with meters and session insights overlaid */}
            <div className="flex-1 relative">
              {avatarCanvasElement}
              {/* Patient metrics (top-left) - Responsive */}
              <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-black/60 rounded-lg py-1 md:py-2 px-2 md:pl-0 md:pr-1 md:pr-2 backdrop-blur-sm z-10">
                <div className="flex flex-row items-center">
                  {/* Rotated text on the left - hidden on mobile */}
                  <h3 className="hidden md:block text-white text-xs md:text-sm font-bold whitespace-nowrap" style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', marginLeft: '8px' }}>
                    Patient Metrics
                  </h3>
                  {/* Text values for mobile - hidden on desktop */}
                  <div className="flex flex-col gap-1 md:hidden">
                    <div className="text-white text-xs">
                      <span className="font-semibold">TBP:</span> <span className="text-gray-200">{breadcrumbLevel}</span>
                    </div>
                    <div className="text-white text-xs">
                      <span className="font-semibold">Mood:</span> <span className="text-gray-200">{mood}</span>
                    </div>
                    <div className="text-white text-xs">
                      <span className="font-semibold">Intensity:</span> <span className="text-gray-200">{(intensity * 100).toFixed(0)}%</span>
                    </div>
                    <div className="text-white text-xs">
                      <span className="font-semibold">Trust:</span> <span className="text-gray-200">{trust.toFixed(0)}%</span>
                    </div>
                  </div>
                  {/* Meters - hidden on mobile, shown on desktop */}
                  <div className="hidden md:flex flex-row items-end gap-1 md:gap-2 -ml-2 md:-ml-3">
                    <PatientMeter
                      label="TBP"
                      value={breadcrumbLevel}
                      isString={true}
                      color="purple"
                    />
                    <PatientMeter
                      label="Mood"
                      value={mood}
                      isString={true}
                      color="blue"
                    />
                    <PatientMeter
                      label="Intensity"
                      value={intensity * 100}
                      maxValue={100}
                      color="red"
                    />
                    <PatientMeter
                      label="Trust"
                      value={trust}
                      maxValue={100}
                      color="green"
                    />
                  </div>
                </div>
              </div>

              {/* Session Insights panel – left side on desktop, below image on mobile - Responsive */}
              <div className={`absolute top-auto bottom-2 left-1/2 -translate-x-1/2 md:top-1/2 md:left-4 md:-translate-y-1/2 md:translate-x-0 w-[calc(100%-1rem)] md:w-80 bg-black/70 rounded-xl p-2 md:p-4 text-xs md:text-sm text-gray-100 backdrop-blur-sm z-10 shadow-xl ${isSessionInsightsMinimized ? '' : 'max-h-[40vh]'} md:max-h-[80vh] overflow-y-auto`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-bold uppercase tracking-wide text-teal-300">
                    Session Insights
                  </h3>
                  {/* Minimize/Maximize button - mobile only */}
                  <button
                    onClick={() => setIsSessionInsightsMinimized(!isSessionInsightsMinimized)}
                    className="md:hidden text-teal-300 hover:text-teal-200 text-lg font-bold px-2 py-1"
                    aria-label={isSessionInsightsMinimized ? "Maximize" : "Minimize"}
                  >
                    {isSessionInsightsMinimized ? "▲" : "▼"}
                  </button>
                </div>
                {/* Content - hidden when minimized on mobile */}
                <div className={`space-y-2 ${isSessionInsightsMinimized ? 'hidden md:block' : ''}`}>
                  {/* Doctor Style */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[1.0rem] text-gray-200">
                        Clinician Style
                      </div>
                      <div className="relative group flex items-center">
                        <span className="ml-1 text-[11px] text-gray-200 cursor-pointer">
                          ⓘ
                        </span>
                        <div className="absolute right-0 top-4 z-20 w-72 bg-white text-xs text-black p-2 rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                          <p className="font-semibold mb-1 text-200">
                            <b>Clinician Style</b>
                          </p>
                          <p>How your style feels to the patient right now.</p>
                          <p className="mt-1">
                            Examples: <span className="italic">neutral</span>,{" "}
                            <span className="italic">warm_supportive</span>,{" "}
                            <span className="italic">probing</span>.
                          </p>
                          <p className="mt-1"><br />
                            Warmer, consistent styles usually support trust and
                            disclosure.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm leading-snug">
                      {renderDoctorStyleSummary(doctorStyleState)}
                    </div>
                  </div>

                  {/* Alliance */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[1.0rem] text-gray-200">
                        Alliance
                      </div>
                      <div className="relative group flex items-center">
                        <span className="ml-1 text-[11px] text-gray-200 cursor-pointer">
                          ⓘ
                        </span>
                        <div className="absolute right-0 top-4 z-20 w-72 bg-white text-xs text-black p-2 rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                          <p className="font-semibold mb-1 text-200">
                            <b>Alliance</b>
                          </p>
                          <p>Therapeutic alliance between clinician and patient.</p>
                          <p className="mt-1">
                            Rapport: <span className="font-mono">0–100</span>,
                            Strength: <span className="font-mono">0.0–1.0</span>.
                          </p>
                          <p className="mt-1">Approximate slabs:</p>
                          <ul className="list-disc ml-4 mt-1">
                            <li>&lt;30: weak / ruptured</li>
                            <li>30–60: fragile or early working</li>
                            <li>60–80: solid working alliance</li>
                            <li>&gt;80: strong alliance and safety</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm leading-snug">
                      {renderAllianceSummary(therapeuticAlliance)}
                    </div>
                  </div>

                  {/* Triggers */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[1.0rem] text-gray-200">
                        Triggers
                      </div>
                      <div className="relative group flex items-center">
                        <span className="ml-1 text-[11px] text-gray-200 cursor-pointer">
                          ⓘ
                        </span>
                        <div className="absolute right-0 top-4 z-20 w-72 bg-white text-xs text-black p-2 rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                          <p className="font-semibold mb-1 text-200">
                            <b>Triggers</b>
                          </p>
                          <p>
                            Topics, words or situations that repeatedly spike
                            anxiety, tension or distress in this session.
                          </p>
                          <p className="mt-1">
                            More / stronger triggers → higher sensitivity and
                            need for slower pacing and grounding.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm leading-snug">
                      {renderTriggersSummary(triggerSystem)}
                    </div>
                  </div>

                  {/* Coping */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[1.0rem] text-gray-200">
                        Coping
                      </div>
                      <div className="relative group flex items-center">
                        <span className="ml-1 text-[11px] text-gray-200 cursor-pointer">
                          ⓘ
                        </span>
                        <div className="absolute right-0 top-4 z-20 w-72 bg-white text-xs text-black p-2 rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                          <p className="font-semibold mb-1 text-200">
                            <b>Coping</b>
                          </p>
                          <p>Coping strategies the patient is using.</p>
                          <p className="mt-1">
                            <u>Adaptive:</u> grounding, seeking support, challenging
                            thoughts, etc.<br /><br />
                          </p>
                          <p>
                            <u>Maladaptive:</u> rumination, avoidance, shutting down,
                            etc.
                          </p>
                          <p className="mt-1">
                            More adaptive and effective coping over time is a
                            positive sign.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm leading-snug">
                      {renderCopingSummary(copingMechanisms)}
                    </div>
                  </div>

                  {/* Patterns */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[1.0rem] text-gray-200">
                        Patterns
                      </div>
                      <div className="relative group flex items-center">
                        <span className="ml-1 text-[11px] text-gray-200 cursor-pointer">
                          ⓘ
                        </span>
                        <div className="absolute right-0 top-4 z-20 w-72 bg-white text-xs text-black p-2 rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                          <p className="font-semibold mb-1 text-200">
                            <b>Patterns</b>
                          </p>
                          <p>
                            Recurring behavioral or response patterns detected
                            across turns.
                          </p>
                          <p className="mt-1">
                            Examples:{" "}
                            <span className="font-mono">
                              neutral_response_3
                            </span>
                            ,{" "}
                            <span className="font-mono">
                              minimization_3
                            </span>
                            .
                          </p>
                          <p className="mt-1">
                            Higher counts → more entrenched habits that might
                            need focused work.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm leading-snug">
                      {renderPatternsSummary(behavioralPatterns)}
                    </div>
                  </div>

                  {/* Session Score */}
                  {renderSessionMetricsSummary(sessionMetrics) && (
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-xs text-gray-200">
                          Session Score
                        </div>
                        <div className="relative group flex items-center">
                          <span className="ml-1 text-[11px] text-gray-200 cursor-pointer">
                            ⓘ
                          </span>
                          <div className="absolute right-0 top-4 z-20 w-72 bg-white text-xs text-black p-2 rounded shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                            <p className="font-semibold mb-1 text-teal-200">
                              Session Score
                            </p>
                            <p>
                              Internal composite score (0–100) used for
                              training, not for diagnosis.
                            </p>
                            <p className="mt-1">Approximate slabs:</p>
                            <ul className="list-disc ml-4 mt-1">
                              <li>&lt;40: problematic / many misses</li>
                              <li>40–60: mixed, some strengths and gaps</li>
                              <li>60–80: generally on track</li>
                              <li>&gt;80: very strong session overall</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm leading-snug">
                        {renderSessionMetricsSummary(sessionMetrics)}
                      </div>
                    </div>
                  )}
                </div>
                {/* Footer hint - hidden when minimized on mobile */}
                <p className={`mt-3 text-[10px] text-gray-400 ${isSessionInsightsMinimized ? 'hidden md:block' : ''}`}>
                  Hint: These signals are for training only and reflect the model&apos;s internal interpretation of the interaction.
                </p>
              </div>
            </div>
          </div>

        )}

        {/* Redesigned Control Bar */}
        {!isAvatarFullscreen && (
          <div className="hidden md:flex flex-none p-4 bg-white/80 backdrop-blur-md border-t border-gray-100 items-center justify-between gap-6 shadow-sm">

            {/* Left: Main Action (Mic Toggle) */}
            <div className="flex items-center gap-4">
              {isProcessing ? (
                <button className="flex items-center gap-2 px-6 py-3 bg-amber-100 text-amber-700 rounded-full font-semibold animate-pulse shadow-sm cursor-wait">
                  <span className="animate-spin">⏳</span> Processing...
                </button>
              ) : !isRecording ? (
                <button
                  onClick={async () => {
                    console.log("[Unmute] Button clicked");
                    isManualRecordingStop.current = false;
                    bufferRef.current = "";
                    setMessage("");
                    messageRef.current = "";
                    setIsProcessing(false);
                    isProcessingRef.current = false;
                    updateAvatarFullscreen(true);
                    setTimeout(() => startRecording(), 100);
                  }}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                >
                  <Mic className="w-5 h-5" /> Start & Unmute
                </button>
              ) : (
                <button
                  onClick={stopRecordingUsingClick}
                  className="flex items-center gap-2 px-6 py-3 bg-secondary-200 text-secondary-600 hover:bg-secondary-300 hover:text-secondary-800 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                >
                  <MicOff className="w-5 h-5" /> Stop / Mute
                </button>
              )}
            </div>

            {/* Center: Settings Group */}
            <div className="flex items-center gap-6 bg-secondary-50 px-6 py-2 rounded-2xl border border-secondary-200 shadow-inner">

              {/* Save Audio Toggle */}
              <button
                onClick={() => setdownloadRecording(!downloadRecording)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${downloadRecording ? 'bg-primary-100 text-primary-700 font-medium ring-2 ring-primary-500/20' : 'text-secondary-500 hover:bg-secondary-200'}`}
                title="Save Audio Recording"
              >
                <Download className={`w-4 h-4 ${downloadRecording ? 'fill-current' : ''}`} />
                <span className="text-sm">Save Audio</span>
              </button>

              <div className="w-px h-8 bg-secondary-300"></div>

              {/* Mute Audio Toggle */}
              <button
                onClick={() => setIsMuted(!isMuted)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${isMuted ? 'bg-amber-100 text-amber-700 font-medium ring-2 ring-amber-500/20' : 'text-secondary-500 hover:bg-secondary-200'}`}
                title="Mute Incoming Audio"
              >
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                <span className="text-sm">{isMuted ? "Muted" : "Active"}</span>
              </button>

              <div className="w-px h-8 bg-secondary-300"></div>

              {/* Sliders Group */}
              <div className="flex items-center gap-6">
                <div className="flex flex-col gap-1">
                  <label htmlFor="sensitivity" className="text-[10px] uppercase tracking-wider font-bold text-secondary-400">Sensitivity: {micSensitivity}</label>
                  <input
                    id="sensitivity"
                    type="range"
                    min="1" max="5" step="0.5"
                    value={micSensitivity}
                    onChange={(e) => setMicSensitivity(Number(e.target.value))}
                    className="w-24 h-1.5 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="silence" className="text-[10px] uppercase tracking-wider font-bold text-secondary-400">Silence: {silenceDetection}</label>
                  <input
                    id="silence"
                    type="range"
                    min="5" max="100" step="5"
                    value={silenceDetection}
                    onChange={(e) => setsilenceDetection(Number(e.target.value))}
                    className="w-24 h-1.5 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="sendTime" className="text-[10px] uppercase tracking-wider font-bold text-secondary-400">Auto-Send</label>
                  <div className="flex items-center gap-1 bg-white border border-secondary-300 rounded px-1.5 py-0.5">
                    <input
                      id="sendTime"
                      type="number"
                      value={sendMessageAfterSecond}
                      onChange={(e) => setsendMessageAfterSecond(Number(e.target.value))}
                      className="w-8 text-xs font-mono text-center outline-none"
                    />
                    <span className="text-[10px] text-gray-400">s</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Waveform */}
            <div className="flex-none hidden lg:block opacity-60">
              <canvas
                ref={waveCanvasRef}
                width={120} height={40}
                className="rounded"
              />
            </div>

          </div>
        )}
      </div>


      {/* Microphone Permission Popup */}
      <MicrophonePermissionPopup
        isOpen={showMicrophonePermissionPopup}
        onClose={() => setShowMicrophonePermissionPopup(false)}
      />

      {showReconstructionPopup && consultation_id && (
        <CognitiveModelReconstructionPopup
          consultationId={consultation_id}
          onSave={handleReconstructionSave}
          onSkip={handleReconstructionSkip}
          onCancel={() => setShowReconstructionPopup(false)}
          isCustomPersona={isCustomPersona}
        />
      )}
      {/* Mobile Settings Overlay */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center animate-in fade-in duration-200">
          <div className="bg-white w-full sm:w-[500px] rounded-t-2xl sm:rounded-2xl p-6 shadow-2xl animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Session Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Mic Sensitivity */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">Mic Sensitivity</label>
                  <span className="text-xs font-mono bg-blue-50 text-blue-600 px-2 py-1 rounded">{micSensitivity}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  step="0.5"
                  value={micSensitivity}
                  onChange={(e) => setMicSensitivity(Number(e.target.value))}
                  className="w-full accent-primary-600 h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer"
                />
                <p className="text-xs text-gray-500">Adjust if the mic is too sensitive or not sensitive enough.</p>
              </div>

              {/* Silence Detection */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">Silence Detection Threshold</label>
                  <span className="text-xs font-mono bg-blue-50 text-blue-600 px-2 py-1 rounded">{silenceDetection}</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={silenceDetection}
                  onChange={(e) => setsilenceDetection(Number(e.target.value))}
                  className="w-full accent-primary-600 h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Auto-Send Timer */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Auto-send after silence</label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center bg-gray-50 rounded-lg px-4 border border-gray-200">
                    <input
                      type="number"
                      value={sendMessageAfterSecond}
                      onChange={(e) => setsendMessageAfterSecond(Number(e.target.value))}
                      className="bg-transparent w-full py-3 outline-none text-gray-800 font-medium"
                    />
                    <span className="text-sm text-gray-500">seconds</span>
                  </div>
                </div>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-2 gap-4 pt-2">
                <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${isMuted ? 'border-red-500 bg-red-50 ring-1 ring-red-500' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-sm font-medium text-gray-700">Mute Audio</span>
                  <input
                    type="checkbox"
                    checked={isMuted}
                    onChange={(e) => setIsMuted(e.target.checked)}
                    className="w-5 h-5 accent-red-500 rounded"
                  />
                </label>

                <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${downloadRecording ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-sm font-medium text-gray-700">Save Audio</span>
                  <input
                    type="checkbox"
                    checked={downloadRecording}
                    onChange={(e) => setdownloadRecording(e.target.checked)}
                    className="w-5 h-5 accent-primary-600 rounded"
                  />
                </label>
              </div>
            </div>

            <div className="mt-8 pt-4 border-t border-gray-100">
              <button
                onClick={() => setShowSettings(false)}
                className="w-full py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-black transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {toastNotification && <Toast {...toastNotification} onClose={() => setToastNotification(null)} />}
    </DashboardLayout>
  );
}

export default ChatSession;

